// ---------------------------------------------------------------------------
// The reference runtime: an Engine is the world + flow manager, and ALL play
// happens on a Flow handle (design/flows.md; the shape is Patter's, adopted
// deliberately so a host driving both engines holds two objects with the
// same grammar). The dealing semantics of design/storylets-schema.md
// section 3 run per flow, implemented exactly and held to the conformance
// corpus.
//
// The flow model, in one place:
//   - an Engine owns the bundle, every lookup built from it, the SHARED
//     property partitions and the host scopes (@world, ...); a Flow owns its own
//     PRNG, per-box clocks, cooldowns, board, claims, play history and the
//     per-flow property partitions. Flows meet only through shared state.
//   - sharing is a per-property `shared` flag on the declaration (never a
//     scope token): @story defaults shared; box, deck, hand and tag
//     properties default per-flow. Every name is shared XOR per-flow, so a
//     read is a union of two bags and a write routes by name.
//   - every property bag lives in ONE ScopeRegistry per game (the
//     one-registry model): the game hands the engine its registry
//     (EngineOptions.registry) or the engine makes its own and acts as its
//     own game. The shared @story registers under `story`, every other bag
//     under a key starting `storylets/`, which no expression can name.
//     saveGame() carries what is NOT a property, plus the registry's values
//     only when the engine made the registry itself.
//   - the HOST SCOPES (@world, @player, ...: every scope the bundle's
//     scopeRegistry declares) are the game's: each one a resolver it binds
//     (EngineOptions.hostScopes, never saved), a scope it registers in its
//     registry, or, for a standalone engine, a self-backed bag the registry
//     stores and saves.
//   - there is no default flow and no ambient current flow: openFlow(id) is
//     the only way in, an existing id is REPLACED (the old flow closes),
//     and a closed flow's handle is INERT - every verb throws (Patter's
//     stale-handle rule).
//   - engine.getProperty serves host scopes (world.*) and shared refs only; a ref that
//     resolves per-flow throws, naming the fix (Patter's teaching rule).
//   - the PROJECT MAP (design/project-map-contract.md 3) is one zone group
//     above the boxes, in `bundle.map.group` and in no box's `tagGroups`. A
//     box that opts in (`usesMap`) sees its name beside its own groups', so
//     its hands bind zones and its cards are tagged with them by id exactly
//     as with a box group. Each zone is ONE value bag, keyed by the tag's id
//     like any tag's, so a zone property is one value whichever box's hand
//     is dealt to it; the `shared` flag still decides per-flow against one
//     for the engine. What the boxes on a map do NOT share: a hand deals only
//     from its own box's decks, and play history stays the asking box's
//     (D7). A bundle that breaks the map's rules is refused at construction
//     (3.8) rather than half-played.
//
// Key dealing contracts, unchanged from round 2 (all per flow now):
//   - two verbs: deal(hand) claims, peek(box, criteria) just looks; you can
//     never play a card you only peeked (3.1, look/use rule)
//   - availability order: hand condition -> deck gate -> taken -> cooldown ->
//     tags -> card condition -> claims -> priority (3.1), the order an ask
//     reaches the verdicts below in
//   - claims are physical WITHIN a flow: a card sits in at most `copies`
//     hands of that flow's board at once, at most once in any one hand; the
//     ledger is derived from the board contents (3.5)
//   - a SHARED card (its deck's flag, or its own overriding it) is scarce
//     across flows too: at most `sharedCopies` hands anywhere, counted over
//     every live flow's board, and a shared `redraw: "never"` is spent for
//     everyone the first time anyone plays it. A finite redraw deliberately
//     stays per flow - a cooldown is an absolute turn of a per-flow clock, so
//     there is nothing shared to compare it against (design/shared-scarcity.md)
//   - the reserved home group inverts the wildcard: a homed card is
//     available only to an ask binding its home (2.4)
//   - ranking: priority desc -> specificity desc (box toggle) -> seeded
//     shuffle of each maximal tie run (3.2)
//   - one PRNG per flow: expression random(), tie shuffles and the batch
//     deal's hand-order shuffle all advance it; state lives in the save (3.3)
//   - each box has its own turn counter PER FLOW; cooldowns are absolute
//     next-eligible turns of the card's box's clock, set at play time from
//     the post-advance turn; "never" is MAX_SAFE_INTEGER, not Infinity (3.4)
//   - @hand composes bound-tag props -> hand props -> chosen tags/criteria
//     (by group name), later shadowing earlier; writes route back to their
//     source; criteria names cannot be written (3.6)
//   - outcome availability is never snapshotted: outcomes() and play()
//     evaluate gates against current state (3.1, 3.7)
//   - a trace event fires after the state it reports has landed, so a
//     handler reading the flow inside it sees the effect (the Live Link's
//     board snapshot depends on this; the shared fixture pins it)
// ---------------------------------------------------------------------------

import { deserialiseAst, evaluate, valueFits } from "@wildwinter/expr";
import type { EvalContext, ExprNode, ScalarValue, ScopeResolver } from "@wildwinter/expr";
import { matchedSpecificity } from "@wildwinter/expr-specificity";
import { storyletsDialect, NEVER_PLAYED } from "@storylet-studio/dialect";

/** The play-history indexes' key for one (box, group, tag) triple.
 *
 *  The BOX is in it because play history is box-specific (design/project-map-
 *  contract.md 3.7, D7, ruled 2026-10-01): `count_played_in` asks about the
 *  asking box's own plays. A box group was already box-unique, so its key never
 *  needed the box; a project-map zone is one tag every opted-in box tags its
 *  cards with, and without the box a play of a newspaper at the quay in one box
 *  would count as an encounter at the quay in another.
 *
 *  A UNIT SEPARATOR (U+001F) joins them: ids are letters, digits and
 *  underscores, so a control character cannot occur in one and two different
 *  triples can never collide into one key the way a "." or ":" join could. NUL
 *  would say the same thing and was the first choice, but GDScript will not
 *  carry one in a string - it substitutes U+FFFD and warns on every parse - and
 *  the four runtimes keep the same spelling. */
const tagKey = (boxId: string, groupId: string, tagId: string): string =>
  `${boxId}\u001f${groupId}\u001f${tagId}`;

import type { StoryletsHost } from "@storylet-studio/dialect";
import {
  BUNDLE_SCHEMA, BUNDLE_SCHEMAS, PLACE_GROUP, bundleHostScopes, allTagGroups, ambiguousValueAddressMessage, effectiveGameId, groupsOfBox,
  isHoleRef, parseHoleRef, valueAddresses, zoneQualifiedValueAddressMessage,
} from "@storylet-studio/model";
import type {
  Box, Bundle, BundleContent, Card, Deck, Expression, FlowSave, Hand, HandTemplate, HostScopeDecl, HostScopeSpec,
  LoadEviction, LoadProperty, LoadReport, NodeExtensions, PlayRecord, PropertyBag, PropertyDecl, PropsPartition,
  SaveEnvelope, SaveEnvelopeV1, Tag, TagGroup,
} from "@storylet-studio/model";
import { SAVE_SCHEMA, SAVE_SCHEMA_V1, SAVE_SCHEMA_V2 } from "@storylet-studio/model";
import { PropertyBag as StateBag, ScopeRegistry, defaultFor } from "@wildwinter/scoperegistry";
import type { LoadMisfit, PropertyRow, ScopeDeclaration } from "@wildwinter/scoperegistry";
import { makePrng, shuffleInPlace } from "./prng.js";
import type { Prng } from "./prng.js";

/**
 * The error the engine throws when it refuses something itself: an unknown
 * box, hand, card or flow, a closed flow, a gated-shut outcome, a write it will
 * not make, a bundle or save it cannot read. A host can tell these apart from a
 * fault in its own code with `instanceof StoryletError`; the message is the
 * same text it always was. Errors the shared property kernel raises reach the
 * host as they are.
 */
export class StoryletError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "StoryletError";
  }
}

export interface EngineOptions {
  /** Default seed for each flow's PRNG; override per flow in openFlow
   *  (cross-runtime determinism, schema 3.3). Default 0. */
  seed?: number;
  /** Retain each flow's event log for introspection - the game-engine seam
   *  (schema 5): every trace event, sequence-stamped and turn-stamped where
   *  the event has a box context. `true` keeps the default 1000 entries
   *  (oldest dropped first). Off by default; subscribeTrace stays the
   *  zero-retention stream. */
  log?: boolean | { cap?: number };
  /**
   * The host scopes the game binds, by token: for each, a resolver over
   * values the game owns and the story reads (and, where `set` is offered,
   * writes), such as `{ world: { get, set } }` for `@world.*`. A declared
   * scope left unbound is self-backed from its defaults, as a property the
   * registry stores and saves. Engine-level, shared by all flows, never
   * saved: the game keeps these values. A game running several engines
   * registers its scopes in its registry itself instead, and binds none
   * here. Tokens are case-sensitive: a token the bundle doesn't declare is
   * reported (`setup`), naming the declared spelling when only the case
   * differs. Patterplay's option, name for name, on every runtime.
   */
  hostScopes?: Record<string, HostScope>;
  /**
   * The game's registry: ONE per game, holding every engine's properties
   * except those the game keeps itself, saved once. Given one, the engine
   * registers its own scopes in it (@story under `story`, every other bag
   * under a key starting `storylets/`, and each scope `hostScopes` binds),
   * reads every other scope from it, and `saveGame()` leaves the property
   * values to the game. The host scopes the bundle declares are then the
   * game's to register: owned if the registry should store them, foreign if
   * the game keeps them. Omit it and the engine makes its own registry and
   * acts as its own game: it self-backs every host scope the bundle
   * declares, and `saveGame()` carries the registry's values too.
   */
  registry?: ScopeRegistry;
  /**
   * Diagnostics hook (opt-in, dev tooling only): fired when `openFlow` REPLACES
   * a flow that still had cards dealt, with the flow id and how many. The
   * behaviour is unchanged - replacing is deliberate and the same in Patter -
   * this only makes it observable, because the case it catches is a host
   * calling `openFlow` straight after `loadGame` to "re-take" its handle and
   * silently discarding the hand the save just restored (`getFlow` is the
   * call). Zero cost when unset; leave it unset in shipped games.
   */
  onReplacedFlow?: (id: string, dealt: number) => void;
  /** Told of every condition the engine could not evaluate and counted as
   *  false (see {@link PlayError}), whether or not anything is tracing, and of
   *  each set-up problem (reported as the constructor ends). Default: a console
   *  warning. Patterplay's `onError`, the same shape. */
  onError?: (error: PlayError) => void;
  /** Told what the registry's refitting loads put back for this engine: a saved
   *  value that no longer fits its declaration (a stage, an enum value or a flag
   *  deleted since the save, or a value of another type) goes back to its
   *  default, and each one comes here with what the save held. A game that
   *  loads its own registry passes `{ refit: true }` and hears its Storylets
   *  share here as the engine's bags claim it (at construction, as a flow opens,
   *  and as a save loads); the engine's own `loadGame` reports drift in its
   *  LoadReport as before. Only this engine's keys (`story`, `storylets/...`,
   *  the host scopes it self-backs): the other engines on the registry take
   *  theirs. Default: a console warning per value, so a drifted save is never
   *  silent. Patterplay's `onRefit`, the same shape (ruling 3, 2026-10-28). */
  onRefit?: (misfits: LoadMisfit[]) => void;
}

/**
 * A host scope's resolver: a `{ get, set? }` the game provides so the story can
 * read (and, if you allow it, write) values the game owns, `@world.*` among
 * them, at run time. Property metadata (types, read-only) comes from the
 * compiled bundle's declarations; the values themselves live in the game and
 * are never stored or saved by this engine. Patterplay's `HostScope`.
 */
export type HostScope = ScopeResolver;

/** Every key `EngineOptions` takes: another is reported (`setup`), since a
 *  plain-JS caller has no type check. */
const ENGINE_OPTIONS = new Set(["seed", "log", "hostScopes", "registry", "onReplacedFlow", "onError", "onRefit"]);

/**
 * A content error the engine played through. Content can fail at run time in
 * ways the compiler cannot see: a host value of the wrong type, a resolver that
 * throws, a division by zero. A deal never stops for one: the condition counts
 * as false, so the card, deck or hand is not available, and the error is
 * reported here and as a `diagnostic` trace event. The same rule and the same
 * shape as Patterplay's, on all four runtimes (the hands-on review, 2026-10,
 * foundation 5: an error used to reach only a trace, so with tracing off a
 * resolver typo looked like a card that never came up). The engine also
 * reports how it was set up (`setup`: a host scope the bundle doesn't
 * declare, an option it doesn't take), as the constructor ends.
 */
export interface PlayError {
  /** The flow it happened in; "" for a `setup` report, which belongs to no flow. */
  flow: string;
  /** What failed: a condition (a Storylets deal evaluates only conditions), or
   *  how the engine was set up (`setup`). */
  kind: "condition" | "setup";
  /** Whose condition: `card <gameId>`, `deck <gameId>`, `hand <gameId>` or
   *  `outcome <card gameId>/<outcome gameId>`. For `setup`, the host-scope
   *  token or option key at fault. */
  node: string;
  /** The expression's source text, when the bundle carries it. */
  source?: string;
  message: string;
}

export interface OpenFlowOptions {
  /** Seed for this flow's PRNG (defaults to the engine's `seed`). */
  seed?: number;
  /**
   * Open this flow AS IT WAS: a blob from `saveFlow`, applied to the freshly
   * opened (or replaced) flow before the handle comes back
   * (design/engine-server.md 4.1).
   *
   * An option on `openFlow` rather than a `Flow.restore` verb on purpose:
   * restoring INTO a running flow is the trap hosts keep falling into
   * (openFlow REPLACES), and "open this flow as it was" is one act. Drift is
   * tolerated exactly as `loadGame` tolerates it, with one addition, because
   * this restore lands in a LIVE engine: a shared card whose world copies are
   * all held by the OTHER open flows is not put back, and is reported as
   * `claimed-elsewhere`. Ask `previewFlowRestore` first to see that coming.
   */
  restore?: FlowSave;
  /**
   * Open this flow with its POCKET in: a durable half from `flow.saveDurable()`,
   * written into the fresh flow before the handle comes back (ruling H). The
   * flow starts as any new flow does, on its own seed and clocks, with the
   * pocket's values laid over its defaults and its durable spends spent.
   * What no longer fits this build is reported through `onRestoreReport`, as
   * `loadDurable` reports the engine's half.
   *
   * Refused together with `restore`: a restore already carries the flow's
   * durable values, so a pocket beside it would be a second answer for the
   * same property, and which one won would be a rule nobody could see.
   */
  durable?: DurableSave;
  /** Handed the report of a `restore` or a `durable` as it happens. For a
   *  restore it is the same report `previewFlowRestore` returns for the same
   *  blob. Ignored without either; the report has nowhere else to go, since
   *  `openFlow` returns the handle. */
  onRestoreReport?: (report: LoadReport) => void;
}

/** The schema tag every durable half carries, so the shape can change later
 *  and a runtime can tell which one it was given. */
export const DURABLE_SCHEMA = "storylets/durable@1";

/**
 * One DURABLE HALF (ruling H, 2026-10-06): what outlives a run. The engine's
 * half (`engine.saveDurable()`) is the installation's memory, a flow's half
 * (`flow.saveDurable()`) is the player's pocket, and the two have one shape.
 *
 * Keyed the way everything host-facing is, because a half is meant to cross
 * builds: a value by its property ADDRESS, exactly as `listProperties()`
 * prints it and `setProperty` takes it (`story.souls`, `hand.the-elder.met`,
 * `value.harbour/docks.lamps`), and a spend by the card's GAMEID. A save keys
 * its property values by internal id so they survive a rename (its cards,
 * hands and boxes by Game ID, from save version 3); a durable half keys by name
 * so a person can read it, and a rename shows up in the report instead.
 */
export interface DurableSave {
  schema: typeof DURABLE_SCHEMA;
  /** The build it was taken from: a load reports drift against it, and
   *  refuses another project's. */
  content: BundleContent;
  /** Every durable property on this half, by address, keys sorted. */
  values: Record<string, ScalarValue>;
  /** The durable `redraw: "never"` spends on this half, by card gameId,
   *  sorted. */
  spent: string[];
}

/** A card view in a dealt hand or a peeked list. Carries NO outcome
 *  availability - ask `outcomes()` for current truth (schema 5). */
export interface DealtCard {
  id: string;
  gameId: string;
  title?: string;
  purpose?: string;
  fields?: Record<string, ScalarValue>;
  /** The card's Storylet extension values, by extension id, exactly as the
   *  bundle carries them: raw and sparse, any JSON, never read by the engine.
   *  Absent when it has none. `effectiveFields(extensionDecls(...), ...)`
   *  fills in defaults. READ-ONLY: these are the bundle's own values, handed
   *  out uncopied (unlike `fields`), so changing them changes them for every
   *  later deal. */
  extensions?: NodeExtensions;
}

export interface OutcomeView {
  id: string;
  gameId: string;
  title?: string;
  purpose?: string;
  /** The outcome's fields, exactly as the bundle carries them: game data
   *  declared by the box's `outcomeFields`, never read by the engine. */
  fields?: Record<string, ScalarValue>;
  /** The outcome's Storylet extension values, by extension id, raw and
   *  read-only, as a card's are. Absent when it has none. */
  extensions?: NodeExtensions;
  /** Evaluated against CURRENT state at the moment of the ask. */
  available: boolean;
}

/** What a peek returns: the top of the stock, looked at and put back.
 *  The engine has no pick policy (Reboot 2.1). */
export interface RankedList {
  box: string;
  cards: DealtCard[];
}

export interface PlayOptions {
  /** Turn advance override; default settings.playAdvancesTurns, or 0 when the
   *  card's box is timed (design/engine-server.md 4.8). */
  advanceTurns?: number;
}

// --- the trace (schema 5): the deal/play log for tooling ----------------------

/** Why a card did or did not make an ask, in availability order (schema 3.1). */
/** Why a held card left its hand: an `evict` trace event's reason, and a
 *  load report's when the load checks what it put back. */
export type EvictReason = CheckReason | "outranked";
/** The reasons a held card fails the check a deal and a load both make. */
type CheckReason = "hand-condition" | "vanished" | "deck-gate" | "taken" | "cooldown" | "tags" | "condition";

export type TraceVerdict =
  | "dealt"        // in the hand / the returned list
  | "capped"       // eligible, ranked below the size cap
  | "cooldown"     // schema 3.1 step 1
  | "deck-gate"    // step 2
  | "tags"         // step 3 (incl. the home group's inverted default)
  | "condition"    // steps 4-5 (a failing or erroring condition)
  | "priority"     // a priority expression errored or was not a number
  | "claimed"      // step 6: no free copy on YOUR board
  | "claimed-elsewhere"  // step 6: another flow holds the world's copies
  | "taken";       // a shared redraw:never was spent, by anyone, for everyone

/** One event on the deal/play log - "why did Ambush at the ford get dealt
 *  here?" is answered by the ask event's per-card verdicts and keys. The
 *  verb is the event type, so a peek is distinguishable from a deal when
 *  reading a run back.
 *
 *  IDENTITY IS BY GAMEID throughout (design/engine-server.md 4.4). It was
 *  mixed until then: `deal.hand` and `peek.box` were gameIds while
 *  `evict.hand`, `play.card` and every `cards[].id` were internal ids, so
 *  every consumer outside the engine - the Board, the four examiners, the
 *  Live Link, a wire a kiosk reads - mapped one to the other itself. */
export type TraceEvent =
  | {
      type: "deal";
      /** Hand gameId. */
      hand: string;
      /** `id` is the card's GAMEID (design/engine-server.md 4.4). */
      cards: { id: string; verdict: TraceVerdict; priority?: number; specificity?: number }[];
    }
  | {
      type: "peek";
      /** Box gameId. */
      box: string;
      criteria: Record<string, string>;
      /** `id` is the card's GAMEID (design/engine-server.md 4.4). */
      cards: { id: string; verdict: TraceVerdict; priority?: number; specificity?: number }[];
    }
  /** Hand and card gameIds. A card the build no longer has (`vanished`) has
   *  no gameId left and is named by the id the board carried. */
  | { type: "evict"; hand: string; card: string; reason: TraceVerdict | "hand-condition" | "vanished" | "outranked" }
  /** A card the game took out of a hand without playing it (`discard`):
   *  card and hand gameIds. */
  | { type: "discard"; hand: string; card: string; turn: number }
  /** Card and outcome gameIds; `outcome` is "" for a card with no outcomes,
   *  played with none. */
  | { type: "play"; card: string; outcome: string; turn: number }
  /** One landed outcome change; `path` is the resolved store location, in the
   *  address grammar `getProperty` takes - the owner segment is its gameId (a
   *  routed @hand write shows where it actually went, schema 3.6). `prev`
   *  is the value it replaced, so a log can read "0 -> 1". */
  | { type: "write"; target: string; path: string; value: ScalarValue; prev?: ScalarValue }
  /** An explicit clock advance via advanceTurns (schema 3.4); `turn` is the
   *  box's new value. Plays stamp their own turn on the play event. */
  | { type: "turns"; box: string; turn: number }
  /** An expression eval error: never a silent pass (schema 3.1), always a
   *  visible diagnostic. */
  | { type: "diagnostic"; where: string; message: string };

export type TraceHandler = (event: TraceEvent) => void;
/** The engine-level tap: every flow's events, tagged with the flow id -
 *  the tools' one stream. */
export type EngineTraceHandler = (flow: string, event: TraceEvent) => void;

/** A retained log entry: the trace event plus its place in flow time.
 *  `seq` orders the whole flow (monotonic; survives clearLog). `turn` is
 *  the clock of the box the event happened in when it fired (peek: the box;
 *  deal/evict: the hand's box; play and its writes: the played card's box,
 *  stamped together with the play's own turn). Diagnostics carry no turn. */
export type LogEntry = TraceEvent & { seq: number; turn?: number };

/** One entry on the ENGINE's log: the same event, plus the flow it happened
 *  in. A run is several flows over shared state, so "what happened in this
 *  run" is only answerable in one ordered stream, and only if each line says
 *  who. The flow's own log stays flow-local and unchanged. */
export type EngineLogEntry = LogEntry & { flow: string };

// --- internals ---------------------------------------------------------------

interface CardEntry {
  card: Card<Expression>;
  deck: Deck<Expression>;
  box: Box<Expression>;
}

/** Is this card scarce across flows (design/shared-scarcity.md)? The deck says
 *  what the pile is for and the card may override it, the same inheritance a
 *  property has with its scope default. Hoist the deck's flag out of a card
 *  loop and pass the answer down: the ask runs this per card per deal. */
const cardIsShared = (card: Card<Expression>, deckShared: boolean): boolean =>
  card.shared ?? deckShared;

/** A card whose spend outlives the run: `redraw: "never"`, with `durable` on
 *  the card or, failing that, its deck, the same fall-through `shared` has.
 *  Only `never` can cross a run boundary: a finite cooldown is an absolute turn
 *  of a clock that restarts with the run. */
const cardIsDurable = (card: Card<Expression>, deck: Deck<Expression>): boolean =>
  card.redraw === "never" && (card.durable ?? deck.durable ?? false) === true;

/** How many hands ACROSS EVERY FLOW may hold this at once. Defaults to
 *  `copies`, so the common case writes one number and only "five in the world,
 *  one to a customer" needs both. Meaningless on an unshared card, and the
 *  compiler warns when one sets it. */
const sharedCap = (card: Card<Expression>): number => card.sharedCopies ?? acrossLimit(card);

/** How many hands of one flow may hold a card at once: one, or any number
 *  (`acrossHands`), or a bundle from before it, its `copies`. */
const acrossLimit = (card: Card<Expression>): number =>
  card.acrossHands === "any" ? Infinity : card.acrossHands === "one" ? 1 : card.copies ?? 1;

/**
 * One run of equally ranked cards, in the order a deal takes them (the
 * hands-on review, 2026-10, rulings 1 and 4). Cards the hand already holds
 * come first, in the order they were scored, so a re-ranking deal never swaps
 * one for an equal. The rest are shuffled, as runs always were, when their
 * weights are all equal; otherwise drawn by weight, one draw per place but the
 * last: each place takes the card whose share of the remaining weight the draw
 * falls in, walking the remaining cards in scored order. Both forms draw
 * (rest - 1) numbers, held cards none, so unweighted content deals exactly
 * as it did.
 */
function orderRun<T extends { entry: CardEntry }>(run: T[], held: ReadonlyMap<string, number> | undefined, prng: Prng): T[] {
  const first = held === undefined ? [] : run.filter((s) => held.has(s.entry.card.id));
  const rest = held === undefined ? run : run.filter((s) => !held.has(s.entry.card.id));
  const weight = (s: T): number => s.entry.card.weight ?? 1;
  if (rest.length > 1 && rest.some((s) => weight(s) !== weight(rest[0]!))) {
    const drawn: T[] = [];
    const left = [...rest];
    while (left.length > 1) {
      let total = 0;
      for (const s of left) total += weight(s);
      let r = prng.next() * total;
      let k = 0;
      while (k < left.length - 1 && r >= weight(left[k]!)) { r -= weight(left[k]!); k++; }
      drawn.push(left.splice(k, 1)[0]!);
    }
    drawn.push(left[0]!);
    return [...first, ...drawn];
  }
  if (rest.length > 1) shuffleInPlace(rest, prng);
  return [...first, ...rest];
}

/** How many slots of one hand a card may fill (`inOneHand`, default 1). */
const inOneHandOf = (card: Card<Expression>): number => card.inOneHand ?? 1;

type HandSource = { kind: "value"; id: string } | { kind: "hand"; id: string } | { kind: "criteria" };

/** One outcome change, resolved and checked but not yet landed. `path` is
 *  the store location the trace reports, in the address grammar. */
type WritePlan =
  | { kind: "host"; host: HostScopeState; name: string; path: string }
  | { kind: "bag"; bag: StateBag; name: string; path: string }
  | { kind: "scope"; scope: string; name: string; path: string };

/** The composed @hand for one ask: the read bag, plus where each name
 *  routes on write (schema 3.6). */
interface HandEnv {
  bag: PropertyBag;
  sources: Map<string, HandSource>;
  /** tag group id -> bound tag id (home included, its "tag" a hand id). */
  boundTags: Map<string, string>;
}

/** One ask, resolved: a deal (hand present, condition from its template or
 *  rule) or a peek (criteria only, no condition - schema 3.1). */
interface AskDescriptor {
  box: Box<Expression>;
  hand?: Hand<Expression>;
  condition?: Expression;
  /** tag group id -> tag id, everything the ask binds (fixed + chosen +
   *  criteria; for deals also home -> the hand's own id). */
  boundTags: Map<string, string>;
  /** Chosen tags / criteria surfaced into @hand by group gameId, the tag's
   *  gameId as the value (schema 3.6). */
  askNames: Record<string, string>;
}

/** The owner label on everything this engine registers: named in a clash
 *  error and carried on the registry's examiner rows. */
const OWNER = "Storylet Engine";

/** Identity: storylets property names are case-significant as authored. */
const identity = (n: string): string => n;

/** A property path as the game may write it (ruling 3, 2026-10-28: Patter's
 *  form as well as this engine's). `story.gold` and `@story.gold` are the same
 *  path, as are `world.time` and `@world.time`; a bare `@gold` is the story's,
 *  as it is in an expression. Anything else is passed through as it was. */
function propertyPath(path: string): string {
  if (!path.startsWith("@")) return path;
  const rest = path.slice(1);
  return rest.includes(".") ? rest : `story.${rest}`;
}

/** The registry keys this engine's bags live under. An id is escaped (`%` and
 *  `/`) so no two keys can meet. Every runtime writes the same keys: they are
 *  in the save. Owners are keyed by INTERNAL id, as the save always was, so a
 *  save survives a rename. */
const esc = (id: string): string => id.replace(/%/g, "%25").replace(/\//g, "%2F");
const unesc = (id: string): string => id.replace(/%2F/g, "/").replace(/%25/g, "%");
const sharedKey = (kind: OwnedScope, id: string): string => `storylets/${kind}/${esc(id)}`;
const flowPrefix = (flowId: string): string => `storylets/flow/${esc(flowId)}/`;
const flowKey = (flowId: string, kind: FlaggedScope, id?: string): string =>
  kind === "story" ? `${flowPrefix(flowId)}story` : `${flowPrefix(flowId)}${kind}/${esc(id!)}`;

/** A registry section's values: registry key -> name -> value. */
type Sections = Record<string, PropertyBag>;

const emptyPartitionValues = (): PropsPartition => ({ story: {}, box: {}, deck: {}, hand: {}, value: {} });

/** Sort a registry save's sections back into partitions, for the load walk:
 *  the shared ones, each flow's (only the flows the save restores; the rest
 *  are dropped), and everything that is not this engine's, passed through. */
function partitionsFromSections(sections: Sections, flowIds: Set<string>): {
  shared: PropsPartition; flows: Map<string, PropsPartition>; rest: Sections;
} {
  const shared = emptyPartitionValues();
  const flows = new Map<string, PropsPartition>();
  const rest: Sections = {};
  const flowOf = (escaped: string): PropsPartition | undefined => {
    const id = unesc(escaped);
    if (!flowIds.has(id)) return undefined;
    let p = flows.get(id);
    if (!p) { p = emptyPartitionValues(); flows.set(id, p); }
    return p;
  };
  for (const [key, values] of Object.entries(sections)) {
    let m: RegExpExecArray | null;
    if (key === "story") shared.story = values;
    else if ((m = /^storylets\/(box|deck|hand|value)\/([^/]+)$/.exec(key))) shared[m[1] as OwnedScope][unesc(m[2]!)] = values;
    else if ((m = /^storylets\/flow\/([^/]+)\/story$/.exec(key))) { const p = flowOf(m[1]!); if (p) p.story = values; }
    else if ((m = /^storylets\/flow\/([^/]+)\/(box|deck|hand|value)\/([^/]+)$/.exec(key))) {
      const p = flowOf(m[1]!); if (p) p[m[2] as OwnedScope][unesc(m[3]!)] = values;
    } else if (!key.startsWith("storylets/")) rest[key] = values;
  }
  return { shared, flows, rest };
}

/** A cleaned partition as registry sections, keyed the way its bags register.
 *  Empty sections are left out: they would load nothing, and a section for a
 *  bag that never registers would wait in the registry for ever. */
function sectionsOf(p: PropsPartition, keyOf: (kind: FlaggedScope, id?: string) => string, out: Sections): void {
  if (Object.keys(p.story).length > 0) out[keyOf("story")] = p.story;
  for (const kind of OWNED_SCOPES) {
    for (const [id, values] of Object.entries(p[kind])) if (Object.keys(values).length > 0) out[keyOf(kind, id)] = values;
  }
}

// Stores are shared-kernel bags (@wildwinter/scoperegistry, the properties
// implementer Patter shares): identity normalisation because storylets
// property names are case-significant as authored.
// `pathPrefix` carries its own separator, so a bag composes its rows' addresses itself
// (`story.gold`, `deck.tavern.drawn`) instead of every caller pasting a prefix onto a row.
const bagFromDecls = (decls: PropertyDecl[], pathPrefix: string): StateBag =>
  new StateBag(decls, { normalise: identity, pathPrefix });

/** One name read from a bag as an OWN value. The bag's `values` is an ordinary
 *  object, so a plain index would answer `constructor`, `toString` and the rest
 *  of Object.prototype with a function: an expression reading `@deck.constructor`
 *  must find nothing there, as it would in every other runtime. Identity
 *  normalisation (above) means the authored name IS the key. */
const ownValue = (bag: StateBag | undefined, name: string): ScalarValue | undefined =>
  bag !== undefined && Object.prototype.hasOwnProperty.call(bag.values, name) ? bag.values[name] : undefined;

/** The scope an expression sees where there is nothing to read (@deck in a
 *  hand condition, a box or deck with no reader): empty, and with no prototype,
 *  so no built-in property answers either. */
const EMPTY_SCOPE: PropertyBag = Object.freeze(Object.create(null) as PropertyBag);

// Truthiness for a bare condition. Booleans and numbers as you would expect;
// a string passes when non-empty and a flag list when non-empty, matching
// JavaScript's own coercion for those two.
//
// Until 2026-09-01 this admitted ONLY booleans and numbers, and Patterplay's
// `truthy` admitted strings and lists as well. That was drift from writing the
// two engines at different times, not a considered difference: the two share a
// property registry, so the same value read from the same registry answered a
// condition differently depending on which engine asked. expr-specificity calls
// truthiness host-bound, and it is, but "host-bound" is licence for a host to
// choose, not licence for two hosts in one family to disagree by accident.
function conditionPasses(v: ScalarValue): boolean {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") return v !== "";
  return v.length > 0; // string[] (flags)
}

/** One kernel bag with its store path prefix (story / box.<gameId> / deck.<gameId>
 *  / hand.<gameId> / value.<gameId>): the state logger's mount surface
 *  (design/engine-runtimes.md 3.4 - the logger builds on the PropertyBag
 *  audit hook, so it needs the bags themselves, not just their rows).
 *  The Engine lists the shared bags, a Flow its own; the @world container
 *  is the host's bag and the host mounts it itself. loadGame() rebuilds
 *  every flow, and so every flow's bags, so re-enumerate after a load. */
export interface BagMount {
  prefix: string;
  bag: StateBag;
}

/** One box on the enumeration surface (examiners, hosts): identity plus
 *  its clock (per flow). */
export interface BoxView {
  id: string;
  gameId: string;
  title?: string;
  turn: number;
  /** The box's Storylet extension values, by extension id, raw and read-only, as a card's are. Absent when it has none. */
  extensions?: NodeExtensions;
}

/** The sharing default per scope (design/flows.md, the old system's rule):
 *  @story is the playthrough family's globals, shared; the narrower scopes
 *  are geographic, per-flow, so "this flow's danger in the docks" stays
 *  expressible. A declaration's `shared` flag overrides. */
const SCOPE_DEFAULT_SHARED = { story: true, box: false, deck: false, hand: false, value: false } as const;
type FlaggedScope = keyof typeof SCOPE_DEFAULT_SHARED;

const isShared = (scope: FlaggedScope, d: PropertyDecl): boolean => d.shared ?? SCOPE_DEFAULT_SHARED[scope];
const sharedHalf = (scope: FlaggedScope, decls: PropertyDecl[]): PropertyDecl[] =>
  decls.filter((d) => isShared(scope, d));
const flowHalf = (scope: FlaggedScope, decls: PropertyDecl[]): PropertyDecl[] =>
  decls.filter((d) => !isShared(scope, d));

/** One side's five declaration lists, keyed by owner id where the scope has
 *  owners. The bags are built from these; so is the load report's answer to
 *  "what does this build declare that the save does not carry". */
interface DeclSet {
  story: PropertyDecl[];
  box: Map<string, PropertyDecl[]>;
  deck: Map<string, PropertyDecl[]>;
  hand: Map<string, PropertyDecl[]>;
  value: Map<string, PropertyDecl[]>;
}

/** The four owned property scopes: the ones whose address carries an owner
 *  segment. `story` has no owner and `world` is the host's. */
type OwnedScope = "box" | "deck" | "hand" | "value";
const OWNED_SCOPES = ["box", "deck", "hand", "value"] as const;
const isOwnedScope = (kind: string | undefined): kind is OwnedScope =>
  (OWNED_SCOPES as readonly string[]).includes(kind ?? "");

/** The owner segment of a property address, both ways round
 *  (design/engine-server.md 4.4).
 *
 * `gameId` is the segment the ADDRESS uses; `id` is the internal id everything
 * inside the engine is keyed by - the bags, the save envelope, the ladders.
 * Both maps are built in bundle order and a repeated gameId does NOT
 * overwrite the first.
 *
 * Box, deck, hand and card gameIds are unique bundle-wide, so for three of the
 * four scopes the segment is simply the gameId. A TAG's is unique only within
 * its group, and a group's only within its box, so two boxes may each name a
 * tag "docks": the value scope's segment is box-qualified,
 * `value.<boxGameId>/<tagGameId>.<name>`, wherever a gameId repeats, and the
 * short form is REFUSED there rather than resolved to the first in bundle
 * order. `valueAddresses` in the model is the one definition of that rule -
 * the Board draws these addresses from the bundle while the engine builds them
 * from this index, and the two have to agree - and `repeated` is what it
 * found, so a refusal can name the candidates.
 *
 * Two GROUPS in one box naming the same tag is the case the box qualifier
 * cannot separate, and it is closing at the source rather than here (question
 * 16, ruled 2026-09-06): the compiler warns that a tag gameId must be unique
 * within its box, and refuses it from the next release. Until then the first
 * in bundle order answers, as it always did.
 */
interface OwnerIndex {
  gameId: Map<string, string>;   // internal id -> owner segment
  id: Map<string, string>;       // owner segment -> internal id (first in bundle order wins)
  repeated: Map<string, string[]>;  // an ambiguous short form -> the qualified candidates
  zoneQualified: Map<string, string>;  // "<box>/<zone>" -> "<zone>", refused (value scope only)
}

type OwnerIndexes = Record<OwnedScope, OwnerIndex>;

const emptyOwnerIndex = (): OwnerIndex =>
  ({ gameId: new Map(), id: new Map(), repeated: new Map(), zoneQualified: new Map() });
const emptyOwnerIndexes = (): OwnerIndexes => ({
  box: emptyOwnerIndex(), deck: emptyOwnerIndex(), hand: emptyOwnerIndex(), value: emptyOwnerIndex(),
});

const indexOwner = (index: OwnerIndex, entity: { id: string; gameId?: string; title?: string }): void => {
  const gameId = effectiveGameId(entity);
  index.gameId.set(entity.id, gameId);
  if (!index.id.has(gameId)) index.id.set(gameId, entity.id);
};

/** The value scope's index, whole: the segments to print, the segments to
 *  accept, the gameIds that need qualifying, and the box-qualified forms of
 *  the project map's zones, which are refused. Built from the bundle rather
 *  than tag by tag, because whether a tag's own gameId is enough is a question
 *  about the OTHER boxes. */
const indexValueOwners = (index: OwnerIndex, bundle: Bundle): void => {
  const addresses = valueAddresses(bundle);
  for (const [id, segment] of addresses.print) index.gameId.set(id, segment);
  for (const [segment, id] of addresses.accept) index.id.set(segment, id);
  for (const [gameId, candidates] of addresses.repeated) index.repeated.set(gameId, candidates);
  for (const [segment, zone] of addresses.zoneQualified) index.zoneQualified.set(segment, zone);
};

/** One side's five stores (shared on the engine, per-flow on each flow). */
interface Partition {
  story: StateBag;
  box: Map<string, StateBag>;
  deck: Map<string, StateBag>;
  hand: Map<string, StateBag>;
  value: Map<string, StateBag>;
}

type PartitionKind = keyof Partition;

/** One durable declaration on one side of the sharing flag, with the address a
 *  durable half keys it by and where its bag is. */
interface DurableProp {
  address: string;
  kind: FlaggedScope;
  /** The owner's internal id; absent for `story`. */
  owner?: string;
  decl: PropertyDecl;
}

/** Every durable declaration in one side's DeclSet, in bundle order. Where two
 *  owners print the same address (two groups in one box naming a tag alike,
 *  the residual the compiler is closing) the first answers, which is the rule
 *  `resolveOwner` keeps for the same address. */
const durableProps = (internals: Internals, decls: DeclSet): DurableProp[] => {
  const out: DurableProp[] = [];
  const seen = new Set<string>();
  const push = (p: DurableProp): void => {
    if (seen.has(p.address)) return;
    seen.add(p.address);
    out.push(p);
  };
  for (const decl of decls.story) if (decl.durable === true) push({ address: `story.${decl.name}`, kind: "story", decl });
  for (const kind of OWNED_SCOPES) {
    for (const [owner, list] of decls[kind]) {
      for (const decl of list) {
        if (decl.durable === true) push({ address: `${addressOf(internals, kind, owner)}.${decl.name}`, kind, owner, decl });
      }
    }
  }
  return out;
};

const durableBag = (p: Partition, prop: DurableProp): StateBag | undefined =>
  prop.kind === "story" ? p.story : p[prop.kind].get(prop.owner!);

/** One half's values, keys in byte order so the same state writes the same
 *  text. An address never looks like an integer (it has a dot in it), so the
 *  object keeps that order (ruling E). */
const durableValues = (p: Partition, props: DurableProp[]): Record<string, ScalarValue> => {
  const entries: [string, ScalarValue][] = [];
  for (const prop of props) {
    const value = ownValue(durableBag(p, prop), prop.decl.name);
    if (value !== undefined) entries.push([prop.address, value]);
  }
  return Object.fromEntries(byKey(entries, ([address]) => address));
};

/** What a durable half will write, decided before anything is written. */
interface DurablePlan {
  values: Map<DurableProp, ScalarValue>;
  /** Internal card ids to spend. */
  cards: string[];
}

/**
 * Walk one durable half against this build's durable declarations and
 * durable cards on one side (`flow` names the flow for a pocket; absent, it is
 * the engine's memory), filing what does not fit in the load report's own
 * fields. Pure: the caller writes the plan.
 *
 * A value lands only at an address this build declares DURABLE ON THIS SIDE:
 * a renamed property, one no longer durable, and one that moved to the other
 * side of `shared` are all dropped, since this half would never have carried
 * them. A value its declaration no longer takes is retyped and takes the
 * default; a durable declaration the half does not carry is defaulted. A spend
 * lands only on a card that is still a durable one-shot on this side, and is
 * otherwise a dropped cooldown (a pocket's spends are cooldowns) or a dropped
 * spent card (a memory's are the engine's spent set), named by the gameId the
 * half carried.
 */
function planDurable(
  internals: Internals, props: DurableProp[], cards: CardEntry[],
  save: DurableSave, flow: string | undefined, draft: ReportDraft,
): DurablePlan {
  const at = (path: string): LoadProperty => ({ ...(flow !== undefined ? { flow } : {}), path });
  const byAddress = new Map(props.map((p) => [p.address, p]));
  const values = new Map<DurableProp, ScalarValue>();
  const carried = save.values ?? {};
  for (const [address, value] of Object.entries(carried)) {
    const prop = byAddress.get(address);
    if (prop === undefined) { draft.droppedProperties.push(at(address)); continue; }
    if (!valueFits(prop.decl, value)) { draft.retypedProperties.push(at(address)); continue; }
    values.set(prop, value);
  }
  for (const prop of props) {
    if (!Object.prototype.hasOwnProperty.call(carried, prop.address)) draft.defaultedProperties.push(at(prop.address));
  }
  const eligible = new Set(cards.map((e) => e.card.id));
  const spend: string[] = [];
  for (const gameId of save.spent ?? []) {
    const entry = internals.cardsByGameId.get(gameId);
    if (entry !== undefined && eligible.has(entry.card.id)) spend.push(entry.card.id);
    else if (flow !== undefined) draft.droppedCooldowns.push({ flow, card: gameId });
    else draft.droppedSpent.push(gameId);
  }
  return { values, cards: spend };
}

/** The two refusals every durable load shares, made before anything moves: a
 *  shape this runtime does not know, and another project's state. */
const assertDurable = (internals: Internals, save: DurableSave): void => {
  const schema = (save as { schema?: unknown } | null | undefined)?.schema;
  if (schema !== DURABLE_SCHEMA) throw new StoryletError(`unsupported durable schema: ${String(schema)}`);
  const project = internals.bundle.content.project;
  if (save.content?.project !== project) {
    throw new StoryletError(`durable state is for project "${String(save.content?.project)}", bundle is "${project}"`);
  }
};

/** A host write of one durable value: silent, as every host write is, and
 *  past a `writable: false`, which is the story's promise and not the game's. */
const putDurable = (bag: StateBag | undefined, name: string, value: ScalarValue): void => {
  bag?.set(name, structuredClone(value), { silent: true, reason: "host durable", host: true });
};

/** Everything a Flow shares with its Engine: the bundle-derived lookups
 *  (immutable), the shared stores (reseeded in place by loadGame/reset),
 *  and the seams. One object, held by both classes - the two are one
 *  machine in two lifetimes. */
interface Internals {
  bundle: Bundle;
  logCap?: number;
  cardsById: Map<string, CardEntry>;
  cardsByGameId: Map<string, CardEntry>;
  boxesByGameId: Map<string, Box<Expression>>;
  boxesById: Map<string, Box<Expression>>;
  handsById: Map<string, { hand: Hand<Expression>; box: Box<Expression> }>;
  handsByGameId: Map<string, { hand: Hand<Expression>; box: Box<Expression> }>;
  /** The owner segment of a property address, both ways round (4.4). */
  owners: OwnerIndexes;
  templatesById: Map<string, HandTemplate<Expression>>;
  /** Every group by internal id. `box` is absent for the project map's group,
   *  which belongs to no box. */
  groupsById: Map<string, { group: TagGroup; box?: Box<Expression> }>;
  requiredGroups: Set<string>;
  nodeCache: WeakMap<Expression, ExprNode>;
  ladders: {
    /** Each host scope's ladders, by token, then name. */
    host: Map<string, Map<string, readonly string[]>>;
    story: Map<string, readonly string[]>;
    box: Map<string, Map<string, readonly string[]>>;
    deck: Map<string, Map<string, readonly string[]>>;
    value: Map<string, Map<string, readonly string[]>>;
    hand: Map<string, Map<string, readonly string[]>>;
  };
  hasQualities: boolean;
  /** Does ANY deck or card in the bundle opt into shared scarcity? False for
   *  the overwhelming majority of projects, and when it is false the two
   *  claim-ledger walks in dealing are skipped entirely. Same idea as
   *  `hasQualities` above: a bundle that does not use a feature must not pay
   *  for it. */
  hasShared: boolean;
  /** The per-flow halves of every declaration list, precomputed once: each
   *  new flow builds its bags from these. */
  flowDecls: DeclSet;
  /** The shared halves, the same way. Not used to build anything - the shared
   *  bags are built straight from the bundle - but a load report has to say
   *  what the shared side WOULD hold without building a bag, which is what
   *  makes previewLoad pure. */
  sharedDecls: DeclSet;
  /** The durable declarations and durable cards on each side (ruling H),
   *  precomputed once from the two DeclSets and the decks. */
  durable: { shared: DurableProp[]; flow: DurableProp[]; sharedCards: CardEntry[]; flowCards: CardEntry[] };
  /** The shared stores, registered in the registry for the engine's life.
   *  Reseeded in place by reset and by a load that carries values. */
  shared: Partition;
  /** The game's one registry (or the engine's own, when it is standalone). */
  registry: ScopeRegistry;
  /** True when the engine made the registry: `saveGame()` then carries its values. */
  ownsRegistry: boolean;
  /** Every OTHER scope in the registry, as an eval context sees it (instance
   *  keys left out), rebuilt only when the registry's set of scopes moves. */
  registryView: () => { scopes: EvalContext["scopes"]; qualities: EvalContext["qualities"] };
  /** Every host scope the bundle declares (`@world`, `@player`, ...), by
   *  token, in bundle order. */
  hostScopes: Map<string, HostScopeState>;
  /** The host scopes as an eval context's scopes, by token. */
  hostResolvers: Record<string, ScopeResolver>;
  /** The game's `onError` (EngineOptions), or the console warning. */
  onError: (error: PlayError) => void;
  /** Take this engine's share of the registry's refits and tell the game
   *  (EngineOptions.onRefit, or the console warning). */
  reportRefits: () => void;
  /** `turn` is the box clock the event happened on, where the caller knows it
   *  - the same stamp the flow's own log carries. Unity and Unreal passed it
   *  from the start; JS and Godot dropped it, so their examiners printed "[-]"
   *  on every deal, peek, evict and write line while the other two printed the
   *  real turn. Four runtimes, two different run logs (2026-08-29). */
  emitEngine: (flow: string, event: TraceEvent, turn?: number) => void;
  engineTracing: () => boolean;
}

/** One host scope the bundle declares, as the engine serves it. */
interface HostScopeState {
  spec: HostScopeSpec;
  /** Read through the registry by name (the scope's own normalisation), so a
   *  scope the game registered folded to lower case still answers the names
   *  as authored. */
  resolver: ScopeResolver;
  /** The WRITE seam. `host` says the caller is the GAME's own surface -
   *  setProperty, the coverage harness, the CLI's --set - which the shared
   *  kernel lets past a `writable: false` (scoperegistry 0.6.0): that flag is
   *  the story's promise, not the game's. The story's refusal is `readOnly`
   *  below, consulted before this seam is reached. A BOUND resolver is opaque -
   *  it takes a name and a value and keeps whatever rule the game has - so the
   *  flag only ever reaches a self-backed bag. Undefined when the scope cannot
   *  be written at all (a resolver bound with no `set`). */
  set?: (name: string, value: ScalarValue, host?: boolean) => void;
  /** True when this engine self-backed it (standalone, no resolver bound). */
  selfBacked: boolean;
}

/** Is a host scope's property read-only TO THE STORY? Its own `writable:
 *  false`, or its scope's: the story's promise, kept at run time as the
 *  compiler keeps it at publish (Reboot.md 10). */
const hostReadOnly = (spec: HostScopeSpec, name: string): boolean =>
  (spec.declarations?.find((d) => d.name === name)?.writable ?? spec.writable) === false;

/** A host scope declaration as the registry takes it, for a foreign scope. */
const toForeignDecl = (d: HostScopeDecl): ScopeDeclaration => ({
  name: d.name, type: d.type,
  ...(d.values !== undefined ? { values: d.values } : {}),
  ...(d.stages !== undefined ? { stages: d.stages } : {}),
  ...(d.default !== undefined ? { default: d.default } : {}),
  ...(d.writable !== undefined ? { writable: d.writable } : {}),
});

/** A self-backed host scope's declarations: the scope's own `writable`
 *  folded into each, since an owned bag reads writability per declaration. */
const selfBackedDecls = (spec: HostScopeSpec): ScopeDeclaration[] =>
  (spec.declarations ?? []).map((d) => {
    const decl = toForeignDecl(d);
    const writable = d.writable ?? spec.writable;
    return writable === undefined ? decl : { ...decl, writable };
  });

/** A template instance inherits the template's property declarations; a
 *  standalone hand declares its own (schema 2.6). `templatesById` is complete
 *  before anything asks: the constructor indexes every box first. */
const handDeclsOf = (internals: Internals, hand: Hand<Expression>): PropertyDecl[] =>
  hand.template !== undefined
    ? internals.templatesById.get(hand.template)?.properties ?? []
    : hand.properties ?? [];

/**
 * One owned property's ADDRESS, owner segment and all: `box.village.mood`.
 *
 * The stores, the save envelope and the ladders stay keyed by internal id -
 * a save must survive a rename, which is the whole reason ids exist - so this
 * is the one place the two vocabularies meet, and it is a formatter, never a
 * lookup key. An owner the build no longer has (a save that outlived an edit)
 * keeps the id it arrived with: there is no gameId left to give it, which is
 * the rule a load report's evictions have always used.
 */
const addressOf = (internals: Internals, kind: OwnedScope, id: string): string =>
  `${kind}.${internals.owners[kind].gameId.get(id) ?? id}`;

/**
 * Resolve a property address's owner segment to the internal id the stores are
 * keyed by. `legacy` says the caller used the pre-4.4 form - an internal id
 * where a gameId belongs - which resolves for THIS release and earns a
 * diagnostic; the next lockstep release refuses it, in every scope including
 * `value`, which had the one reprieve until it gained an address of its own.
 * `ambiguous` is a short-form value address two boxes answer to: the caller
 * REFUSES it, listing those candidates. Undefined when the segment names no
 * owner at all, which is the caller's "no <kind> store" error.
 */
type OwnerLookup = { id: string; legacy: boolean } | { ambiguous: string[] } | { zone: string };

const resolveOwner = (internals: Internals, kind: OwnedScope, segment: string): OwnerLookup | undefined => {
  // Checked before the lookup, because the short form is deliberately NOT in
  // the accept map when it is ambiguous: silently picking the first tag in
  // bundle order is the bug this removes.
  const candidates = internals.owners[kind].repeated.get(segment);
  if (candidates !== undefined) return { ambiguous: candidates };
  // A box-qualified form of a project-map zone: never accepted, because the
  // zone belongs to no box (design/project-map-contract.md 3.4).
  const zone = internals.owners[kind].zoneQualified.get(segment);
  if (zone !== undefined) return { zone };
  const byGameId = internals.owners[kind].id.get(segment);
  if (byGameId !== undefined) return { id: byGameId, legacy: false };
  // A gameId that equals its id took the branch above, so anything reaching
  // here and known as an id is genuinely the old spelling.
  if (internals.owners[kind].gameId.has(segment)) return { id: segment, legacy: true };
  return undefined;
};

/** Resolve or throw: the two refusals every property address shares, in one
 *  place, so the engine's surface and a flow's answer alike. */
const ownerOrThrow = (internals: Internals, kind: OwnedScope, segment: string, name: string): { id: string; legacy: boolean } => {
  const owner = resolveOwner(internals, kind, segment);
  if (owner === undefined) throw new StoryletError(`no ${kind} store "${segment}"`);
  if ("ambiguous" in owner) throw new StoryletError(ambiguousValueAddressMessage(segment, name, owner.ambiguous));
  if ("zone" in owner) throw new StoryletError(zoneQualifiedValueAddressMessage(segment, owner.zone, name));
  return owner;
};

/** What a legacy address is told. It NAMES the address to move to, because
 *  "that form is deprecated" without the replacement leaves a host grepping a
 *  bundle for ids it never chose. */
const legacyAddressMessage = (internals: Internals, kind: OwnedScope, segment: string, name: string): string =>
  `"${kind}.${segment}.${name}" names the ${kind} by its internal id; write "${addressOf(internals, kind, segment)}.${name}". `
  + `The internal-id form is refused after the next release.`;

/** Build one side of the partition from that side's declarations (the
 *  engine precomputes both halves once, in bundle order, as a DeclSet). The
 *  bags are KEYED by internal id and ADDRESSED by gameId; see addressOf. */
const buildPartition = (internals: Internals, decls: DeclSet): Partition => {
  const bags = (kind: OwnedScope): Map<string, StateBag> => new Map([...decls[kind]].map(
    ([id, list]): [string, StateBag] => [id, bagFromDecls(list, `${addressOf(internals, kind, id)}.`)]));
  return {
    story: bagFromDecls(decls.story, "story."),
    box: bags("box"), deck: bags("deck"), hand: bags("hand"), value: bags("value"),
  };
};

const partitionValues = (p: Partition): PropsPartition => ({
  story: p.story.values,
  box: Object.fromEntries([...p.box].map(([id, bag]) => [id, bag.values])),
  deck: Object.fromEntries([...p.deck].map(([id, bag]) => [id, bag.values])),
  hand: Object.fromEntries([...p.hand].map(([id, bag]) => [id, bag.values])),
  value: Object.fromEntries([...p.value].map(([id, bag]) => [id, bag.values])),
});

const loadPartition = (p: Partition, values: PropsPartition | undefined): void => {
  // Fresh defaults are already in the bags; the saved values land over
  // them: orphaned keys (deleted entities, re-flagged properties) drop;
  // newly declared properties keep defaults.
  p.story.load(values?.story ?? {});
  for (const kind of OWNED_SCOPES) {
    for (const [id, bag] of Object.entries(values?.[kind] ?? {})) {
      p[kind].get(id)?.load(bag);
    }
  }
};

// --- the load report (design/engine-server.md 4.9) ---------------------------
//
// One walk, two entry points. `previewLoad` runs it and returns the report;
// `loadGame` runs it, returns the same report and then applies the CLEANED
// blob the walk produced. Two implementations of "what does this save cost"
// would drift the first time one of them was fixed, so there is one, and the
// apply half consumes its output rather than repeating its decisions.

/** The report under construction: unsorted, until finishReport orders it. */
interface ReportDraft {
  evicted: LoadEviction[];
  droppedCooldowns: { flow: string; card: string }[];
  droppedSpent: string[];
  droppedProperties: LoadProperty[];
  defaultedProperties: LoadProperty[];
  retypedProperties: LoadProperty[];
}

const emptyDraft = (): ReportDraft => ({
  evicted: [], droppedCooldowns: [], droppedSpent: [],
  droppedProperties: [], defaultedProperties: [], retypedProperties: [],
});

/** The sort key separator: a UNIT SEPARATOR, as the play-history indexes use,
 *  because it cannot occur in an id, a gameId or a property name. */
const SORT_SEP = "\u001f";

const byKey = <T>(items: T[], key: (item: T) => string): T[] =>
  [...items].map((item) => ({ item, k: key(item) }))
    .sort((a, b) => (a.k < b.k ? -1 : a.k > b.k ? 1 : 0))
    .map((e) => e.item);

const propKey = (p: LoadProperty): string => `${p.flow ?? ""}${SORT_SEP}${p.path}`;

// Whether a saved value still fits its declaration is the kernel's `valueFits`
// (@wildwinter/expr), shared with Patter's loads: the type first, then the
// declaration's own vocabulary, so an enum value or a stage the edit struck out
// no longer fits though it is still a string.

/** Walk one bag's worth of saved values against one bag's worth of
 *  declarations: report the orphans, the newcomers and the misfits, and
 *  return the values that survive. */
function walkScope(
  decls: PropertyDecl[] | undefined,
  saved: PropertyBag | undefined,
  path: (name: string) => string,
  flow: string | undefined,
  draft: ReportDraft,
): PropertyBag {
  const at = (name: string): LoadProperty =>
    ({ ...(flow !== undefined ? { flow } : {}), path: path(name) });
  const byName = new Map((decls ?? []).map((d) => [d.name, d]));
  const values = saved ?? {};
  const clean: PropertyBag = {};
  for (const [name, value] of Object.entries(values)) {
    const decl = byName.get(name);
    if (decl === undefined) { draft.droppedProperties.push(at(name)); continue; }
    if (!valueFits(decl, value)) { draft.retypedProperties.push(at(name)); continue; }
    clean[name] = value;
  }
  for (const decl of decls ?? []) {
    if (!(decl.name in values)) draft.defaultedProperties.push(at(decl.name));
  }
  return clean;
}

/** The same walk over all five scopes of one partition. An owner the save
 *  carries and the build no longer has drops whole (its bag is gone, so its
 *  values have nowhere to land); an owner the build has and the save lacks
 *  keeps every default. */
function walkPartition(
  internals: Internals,
  decls: DeclSet,
  values: PropsPartition | undefined,
  flow: string | undefined,
  draft: ReportDraft,
): PropsPartition {
  const out: PropsPartition = {
    story: walkScope(decls.story, values?.story, (n) => `story.${n}`, flow, draft),
    box: {}, deck: {}, hand: {}, value: {},
  };
  for (const kind of OWNED_SCOPES) {
    const savedKind = values?.[kind] ?? {};
    const ids = [...new Set([...decls[kind].keys(), ...Object.keys(savedKind)])].sort();
    for (const id of ids) {
      // The report's `path` is exactly what listProperties() prints and what
      // setProperty takes: one grammar, so an operator reading a hot-swap
      // report can paste the address straight back in (4.4).
      const owner = addressOf(internals, kind, id);
      out[kind][id] = walkScope(decls[kind].get(id), savedKind[id],
        (n) => `${owner}.${n}`, flow, draft);
    }
  }
  return out;
}

/** Order the draft and answer the identity questions. `saved` is the content
 *  block the save carries; for a single-flow restore there is none, so the
 *  caller passes the bundle's own and no drift is reported. */
function finishReport(bundle: BundleContent, saved: BundleContent, flows: string[], draft: ReportDraft): LoadReport {
  const drift = saved.version !== bundle.version || saved.hash !== bundle.hash;
  const evicted = byKey(draft.evicted, (e) => [e.flow, e.hand, e.card, e.reason].join(SORT_SEP));
  const droppedCooldowns = byKey(draft.droppedCooldowns, (c) => `${c.flow}${SORT_SEP}${c.card}`);
  const droppedSpent = [...draft.droppedSpent].sort();
  const droppedProperties = byKey(draft.droppedProperties, propKey);
  const defaultedProperties = byKey(draft.defaultedProperties, propKey);
  const retypedProperties = byKey(draft.retypedProperties, propKey);
  return {
    // `flows` is what the load restores, not something it had to change, so
    // it never makes a report inexact.
    exact: !drift && evicted.length === 0 && droppedCooldowns.length === 0 && droppedSpent.length === 0
      && droppedProperties.length === 0 && defaultedProperties.length === 0 && retypedProperties.length === 0,
    project: bundle.project,
    version: { saved: saved.version, bundle: bundle.version },
    hash: { saved: saved.hash, bundle: bundle.hash },
    flows,
    evicted, droppedCooldowns, droppedSpent,
    droppedProperties, defaultedProperties, retypedProperties,
  };
}

// --- inputs the game hands over -------------------------------------------------

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

/** What a refused input was, for the message: `null`, `an array`, `a string`. */
const describeInput = (value: unknown): string =>
  value === null ? "null" : value === undefined ? "nothing" : Array.isArray(value) ? "an array" : `a ${typeof value}`;

/** The parts of a bundle the engine reads before anything else, each named
 *  with what it should be; empty when they are all there. */
const bundleShapeProblems = (bundle: Record<string, unknown>): string[] => {
  const problems: string[] = [];
  if (!isRecord(bundle["content"])) problems.push("no content");
  if (!isRecord(bundle["settings"])) problems.push("no settings");
  // A bundle @1 or @0 has a `world` block; from @2 the game's scopes are
  // `scopeRegistry`, absent when the project declares none.
  const scopes = bundle["schema"] === BUNDLE_SCHEMA ? ["story"] as const : ["story", "world"] as const;
  for (const scope of scopes) {
    const part = bundle[scope];
    if (!isRecord(part) || !Array.isArray(part["properties"])) problems.push(`no ${scope} properties`);
  }
  const registry = bundle["scopeRegistry"];
  if (registry !== undefined && (!isRecord(registry) || !Array.isArray(registry["scopes"]))) problems.push("no host scopes in its scopeRegistry");
  if (!Array.isArray(bundle["boxes"])) problems.push("no boxes");
  return problems;
};

/** Refuse a save envelope this runtime cannot read, by what is wrong with it,
 *  before anything reads it (the hands-on review, 2026-10, foundation 5). */
const refuseUnreadableSave = (envelope: unknown): void => {
  if (!isRecord(envelope)) throw new StoryletError(`not a Storylets save: expected an object, got ${describeInput(envelope)}`);
  const schema = envelope["schema"];
  if (schema !== SAVE_SCHEMA && schema !== SAVE_SCHEMA_V2 && schema !== SAVE_SCHEMA_V1) {
    throw new StoryletError(`unsupported save schema: ${String(schema)} (this runtime reads ${SAVE_SCHEMA_V1}, ${SAVE_SCHEMA_V2} and ${SAVE_SCHEMA})`);
  }
  if (!isRecord(envelope["content"])) throw new StoryletError("not a complete Storylets save: no content");
};

/** What a declared property takes, in words, for a refused host write. */
const takes = (decl: PropertyDecl | HostScopeDecl): string => {
  const quoted = (xs: readonly string[] | undefined): string => (xs ?? []).map((x) => JSON.stringify(x)).join(", ");
  switch (decl.type) {
    case "number": return "a number";
    case "boolean": return "true or false";
    case "enum": return `one of ${quoted(decl.values)}`;
    case "flags": return decl.values !== undefined && decl.values.length > 0 ? `a list of flags from ${quoted(decl.values)}` : "a list of flags";
    case "quality": return `one of its stages ${quoted(decl.stages)}`;
    default: return "text";
  }
};

// --- save keys ------------------------------------------------------------------
//
// A save names cards, hands and boxes by GAME ID from version 3 (the hands-on
// review, 2026-10, foundation 7), as its play log always did: a card deleted
// and made again under the same Game ID is the same card to a game, so it
// keeps its cooldown and its place as well as its play count. Earlier saves
// name them by internal id, so a saved key is read as a Game ID first and an
// internal id second; the two never collide, ids being generated.

const savedCard = (internals: Internals, key: string): CardEntry | undefined =>
  internals.cardsByGameId.get(key) ?? internals.cardsById.get(key);
const savedHand = (internals: Internals, key: string): { hand: Hand<Expression>; box: Box<Expression> } | undefined =>
  internals.handsByGameId.get(key) ?? internals.handsById.get(key);
const savedBox = (internals: Internals, key: string): Box<Expression> | undefined =>
  internals.boxesByGameId.get(key) ?? internals.boxesById.get(key);
/** The Game ID a save names an entity by; the id itself for one the build no
 *  longer has, which is all there is to name it by. */
const cardKey = (internals: Internals, id: string): string => {
  const entry = internals.cardsById.get(id);
  return entry !== undefined ? effectiveGameId(entry.card) : id;
};

/**
 * Refuse a host write that does not fit what the content declared (the
 * hands-on review, 2026-10, foundation 5): a string for a number, a value an
 * enum or a quality does not list, a flag a flags property does not, or a name
 * the content never declared in a host scope it declares. The rule is the
 * kernel's `valueFits`, the one a load walks saved values with, so a value the
 * game may set is exactly one a save may carry. An opaque host scope declares
 * no names, so any name and value in it is the game's. Another engine's scope
 * (`patter.x`) is that engine's to check.
 */
const checkHostWrite = (internals: Internals, path: string, value: ScalarValue): void => {
  const parts = path.split(".");
  let decl: PropertyDecl | HostScopeDecl | undefined;
  const host = parts.length === 2 ? internals.hostScopes.get(parts[0]!) : undefined;
  if (host !== undefined) {
    const decls = host.spec.declarations;
    if (decls === undefined) return;
    const name = parts[1]!;
    decl = decls.find((d) => d.name === name) ?? decls.find((d) => d.name.toLowerCase() === name.toLowerCase());
    if (decl === undefined) throw new StoryletError(`no property at "${path}": the content declares no @${host.spec.token}.${name}`);
  } else if (parts.length === 2 && parts[0] === "story") {
    const name = parts[1]!;
    decl = internals.sharedDecls.story.find((d) => d.name === name) ?? internals.flowDecls.story.find((d) => d.name === name);
  } else if (parts.length === 3 && isOwnedScope(parts[0])) {
    const kind = parts[0];
    const [, segment, name] = parts as unknown as [string, string, string];
    const id = ownerOrThrow(internals, kind, segment, name).id;
    decl = internals.sharedDecls[kind].get(id)?.find((d) => d.name === name)
      ?? internals.flowDecls[kind].get(id)?.find((d) => d.name === name);
  }
  // An enum whose own default is "" (none chosen, what an editor writes for
  // "(none)") takes "" back, so a game can put its default back.
  if (decl !== undefined && !valueFits(decl, value, { noneChosen: decl.type === "enum" && decl.default === "" })) {
    throw new StoryletError(`"${path}" takes ${takes(decl)}; ${JSON.stringify(value)} doesn't fit`);
  }
};

/** Every declared host scope's properties as examiner rows, read through its
 *  resolver, in bundle order (an opaque scope declares none, so lists none). */
const hostRows = (internals: Internals): PropertyRow[] => {
  const out: PropertyRow[] = [];
  for (const host of internals.hostScopes.values()) {
    for (const d of host.spec.declarations ?? []) {
      const value = host.resolver.get(d.name);
      out.push({
        path: `${host.spec.token}.${d.name}`, name: d.name, type: d.type,
        value: value ?? defaultFor(d), default: defaultFor(d),
        ...(d.values !== undefined ? { values: d.values } : {}),
        ...(d.stages !== undefined ? { stages: d.stages } : {}),
        // A host scope is FOREIGN - a host resolver backs it - so writability is whether
        // that resolver can be written at all AND what the declaration says, which is
        // the shared registry's own rule for a foreign scope (its foreignWritable).
        //
        // A row is where `writable: false` is meant to SHOW (Reboot.md 10): it tells a
        // state panel this is the game's value, not the story's. It does not stop the
        // panel editing it - the host's setProperty passes `{ host: true }`.
        writable: host.set !== undefined && !hostReadOnly(host.spec, d.name),
      });
    }
  }
  return out;
};

// --- the Engine ---------------------------------------------------------------

/**
 * Refuse, at construction, a bundle this engine cannot read faithfully
 * (design/project-map-contract.md 3.8). Throws one error naming every problem
 * found, each naming the box and the group or tag at fault.
 *
 * The compiler refuses all of these first. The engine checks again because it
 * cannot tell a hand-built or stale bundle from a compiled one, and the
 * alternative is the silent half-working the server audit found twice: a hand
 * whose bound group is looked up in the wrong place comes back empty, and an
 * old runtime given a map bundle deals plausibly while every zone value is
 * missing. Only what would make the engine's OWN resolution ambiguous or wrong
 * is refused here; the compiler's bundle-wide group-name rule is stricter.
 */
const refuseUnreadableBundle = (bundle: Bundle): void => {
  // Not an object at all (`new Engine(null)`, a bundle file read as text):
  // said plainly, rather than as whichever property the engine reached for
  // first (the hands-on review, 2026-10, foundation 5).
  if (!isRecord(bundle)) throw new StoryletError(`not a Storylets bundle: expected an object, got ${describeInput(bundle)}`);
  // The schema tag (D4). Checked alone and first: a bundle of a schema this
  // runtime does not know may not have any of the shape the rest reads.
  const schema = (bundle as { schema?: unknown }).schema;
  if (typeof schema !== "string" || !BUNDLE_SCHEMAS.includes(schema)) {
    throw new StoryletError(`unsupported bundle schema: ${String(schema)} (this runtime reads ${BUNDLE_SCHEMAS.slice(0, -1).join(", ")} and ${BUNDLE_SCHEMAS[BUNDLE_SCHEMAS.length - 1]})`);
  }
  // The parts every bundle of a known schema has, by name: a bundle missing
  // one is cut short or hand-built, and would fail later as a raw TypeError.
  const missing = bundleShapeProblems(bundle);
  if (missing.length > 0) throw new StoryletError(`not a complete Storylets bundle: ${missing.join("; ")}`);
  const problems: string[] = [];
  const map = bundle.map?.group;
  const mapName = map !== undefined ? effectiveGameId(map) : undefined;
  if (map !== undefined && mapName === PLACE_GROUP) {
    problems.push(`the project map's tag group is called "${PLACE_GROUP}", which is reserved for a box's own hands`);
  }
  // Every box tag gameId, for the zone-name rule: a zone's address has no
  // qualified form to fall back on (3.4), so any box tag sharing it anywhere
  // makes `value.<zone>.<name>` ambiguous.
  const boxTags = new Map<string, { box: string; group: string }>();
  for (const box of bundle.boxes) {
    for (const group of box.tagGroups) {
      for (const tag of group.tags) {
        const gameId = effectiveGameId(tag);
        if (!boxTags.has(gameId)) boxTags.set(gameId, { box: effectiveGameId(box), group: effectiveGameId(group) });
      }
    }
  }
  for (const tag of map?.tags ?? []) {
    const zone = effectiveGameId(tag);
    const clash = boxTags.get(zone);
    if (clash !== undefined) {
      problems.push(`the project map's zone "${zone}" has the name of tag "${zone}" in box "${clash.box}", group "${clash.group}", `
        + `so "value.${zone}.<name>" would name two things`);
    }
  }
  for (const box of bundle.boxes) {
    const boxName = effectiveGameId(box);
    if (box.usesMap === true) {
      if (map === undefined) {
        problems.push(`box "${boxName}" uses the project map, but the bundle has no map`);
        continue;
      }
      // One namespace in an opted-in box (3.1): a box group with the map's
      // name would make every name-based lookup there a coin toss.
      const twin = box.tagGroups.find((g) => effectiveGameId(g) === mapName);
      if (twin !== undefined) {
        problems.push(`box "${boxName}" uses the project map and declares its own tag group "${mapName}", the map's name`);
      }
      continue;
    }
    if (map === undefined) continue;
    // A box NOT on the map may not reference its group at all: every route a
    // reference takes, card tags, template bindings and holes, a hand's
    // chosen tags and a rule's bindings, by the group's id as written.
    const names = (where: string): void => {
      problems.push(`box "${boxName}" is not on the project map, but ${where} names the map's tag group "${mapName}"`);
    };
    for (const deck of box.decks) {
      for (const card of deck.cards) {
        if (card.tags?.[map.id] !== undefined) names(`card "${effectiveGameId(card)}"`);
      }
    }
    for (const template of box.handTemplates) {
      if (template.bindings?.[map.id] !== undefined || template.chooses?.includes(map.id) === true) {
        names(`hand template "${effectiveGameId(template)}"`);
      }
    }
    for (const hand of box.hands) {
      if (hand.chosen?.[map.id] !== undefined || hand.rule?.bindings?.[map.id] !== undefined) {
        names(`hand "${effectiveGameId(hand)}"`);
      }
    }
  }
  if (problems.length > 0) throw new StoryletError(`bundle refused: ${problems.join("; ")}`);
};

export class Engine {
  private readonly internals: Internals;
  private readonly seed: number;
  private readonly onReplacedFlow: EngineOptions["onReplacedFlow"];
  private readonly flowsById = new Map<string, Flow>();
  private readonly engineTraceHandlers = new Set<EngineTraceHandler>();
  /** The options this engine was built with: hotSwap builds its replacement from them. */
  private readonly creationOptions: EngineOptions;
  /** How to register each shared scope again, in registration order: a failed
   *  hotSwap puts this engine back exactly as it was. */
  private readonly sharedMounts: Array<{ key: string; mount: () => void }> = [];

  constructor(bundle: Bundle, opts: EngineOptions = {}) {
    // First, before anything is indexed or registered: a bundle this engine
    // cannot read faithfully is refused whole (design/project-map-contract.md
    // 3.8), and a refusal must leave the game's registry untouched.
    refuseUnreadableBundle(bundle);
    this.creationOptions = opts;
    this.seed = opts.seed ?? 0;
    this.onReplacedFlow = opts.onReplacedFlow;
    // Set-up problems found while building, reported as the constructor ends,
    // when the engine can report. A plain-JS caller gets no type check on its
    // options, so a mistyped or retired key would be ignored in silence.
    const setup: Array<{ node: string; message: string }> = [];
    for (const key of Object.keys(opts)) {
      if (ENGINE_OPTIONS.has(key)) continue;
      setup.push({ node: key, message: key === "world"
        ? `the engine option "world" has gone: bind the scope as hostScopes: { world }`
        : `"${key}" isn't an engine option, so it's ignored` });
    }
    const internals: Internals = {
      bundle,
      ...(opts.log ? { logCap: typeof opts.log === "object" ? (opts.log.cap ?? 1000) : 1000 } : {}),
      cardsById: new Map(), cardsByGameId: new Map(),
      boxesByGameId: new Map(), boxesById: new Map(),
      handsById: new Map(), handsByGameId: new Map(),
      owners: emptyOwnerIndexes(),
      templatesById: new Map(), groupsById: new Map(),
      requiredGroups: new Set(),
      nodeCache: new WeakMap(),
      ladders: { host: new Map(), story: new Map(), box: new Map(), deck: new Map(), value: new Map(), hand: new Map() },
      hasQualities: false,
      hasShared: false,
      flowDecls: { story: [], box: new Map(), deck: new Map(), hand: new Map(), value: new Map() },
      sharedDecls: { story: [], box: new Map(), deck: new Map(), hand: new Map(), value: new Map() },
      durable: { shared: [], flow: [], sharedCards: [], flowCards: [] },
      shared: undefined as unknown as Partition,
      registry: opts.registry ?? new ScopeRegistry(),
      onError: opts.onError ?? ((e) => console.warn(e.kind === "setup" ? `Storylet Engine: ${e.message}`
        : `Storylet Engine: the condition on ${e.node} in flow '${e.flow}' failed, counted as false: ${e.message}`)),
      ownsRegistry: opts.registry === undefined,
      reportRefits: () => {
        const misfits = this.internals.registry.takeMisfits((scope) => scope === "story" || scope.startsWith("storylets/")
          || this.internals.hostScopes.get(scope)?.selfBacked === true);
        if (misfits.length === 0) return;
        if (opts.onRefit !== undefined) opts.onRefit(misfits);
        else for (const m of misfits) console.warn(`Storylet Engine: the save's ${m.scope}.${m.name} was ${JSON.stringify(m.value)}, which no longer fits its declaration; it is back at its default`);
      },
      registryView: () => view(),
      hostScopes: new Map(),
      hostResolvers: {},
      emitEngine: (flow, event, turn) => {
        if (this.internals.logCap !== undefined) {
          this.engineLog.push({ ...event, flow, seq: this.engineSeq++, ...(turn !== undefined ? { turn } : {}) });
          if (this.engineLog.length > this.internals.logCap) {
            this.engineLog.splice(0, this.engineLog.length - this.internals.logCap);
          }
        }
        // From a copy (ruling F): a handler that subscribes or unsubscribes
        // during an event changes who hears the NEXT one, never this one.
        for (const h of [...this.engineTraceHandlers]) h(flow, event);
      },
      engineTracing: () => this.engineTraceHandlers.size > 0,
    };
    this.internals = internals;
    let viewRevision = -1;
    let viewCache: ReturnType<Internals["registryView"]> = { scopes: {}, qualities: undefined };
    const view = (): ReturnType<Internals["registryView"]> => {
      const reg = internals.registry;
      if (reg.revision !== viewRevision) {
        const ctx = reg.toEvalContext();
        const scopes: EvalContext["scopes"] = {};
        // An instance key (`storylets/deck/x`, another engine's) is no expression token.
        for (const [k, v] of Object.entries(ctx.scopes)) if (!k.includes("/")) scopes[k] = v;
        viewCache = { scopes, qualities: ctx.qualities };
        viewRevision = reg.revision;
      }
      return viewCache;
    };

    // The value scope's segments come off the whole bundle at once (a tag
    // gameId is only unique within its group), so they are built before the
    // walk rather than tag by tag inside it.
    indexValueOwners(internals.owners.value, bundle);

    for (const box of bundle.boxes) {
      internals.boxesById.set(box.id, box);
      internals.boxesByGameId.set(effectiveGameId(box), box);
      indexOwner(internals.owners.box, box);
      for (const group of box.tagGroups) {
        internals.groupsById.set(group.id, { group, box });
        if (group.required === true) internals.requiredGroups.add(group.id);
      }
      for (const deck of box.decks) {
        indexOwner(internals.owners.deck, deck);
        if (deck.shared === true) internals.hasShared = true;
        for (const card of deck.cards) {
          const entry = { card, deck, box };
          internals.cardsById.set(card.id, entry);
          internals.cardsByGameId.set(effectiveGameId(card), entry);
          if (card.shared === true) internals.hasShared = true;
          if (cardIsDurable(card, deck)) {
            (cardIsShared(card, deck.shared ?? false) ? internals.durable.sharedCards : internals.durable.flowCards).push(entry);
          }
        }
      }
      for (const template of box.handTemplates) {
        internals.templatesById.set(template.id, template);
      }
      for (const hand of box.hands) {
        internals.handsById.set(hand.id, { hand, box });
        internals.handsByGameId.set(effectiveGameId(hand), { hand, box });
        indexOwner(internals.owners.hand, hand);
      }
    }
    // The project map's group: by id like any group, so a hand's binding, a
    // filled hole and tag matching need no logic of their own for it. Which
    // boxes may NAME it is `groupInBox`'s business (3.1); which may reference
    // it at all was settled by the refusal above.
    if (bundle.map !== undefined) {
      internals.groupsById.set(bundle.map.group.id, { group: bundle.map.group });
      if (bundle.map.group.required === true) internals.requiredGroups.add(bundle.map.group.id);
    }
    this.initLadders();

    // Both halves, precomputed once (a bundle's declarations never change):
    // each openFlow builds its bags from the per-flow half, and a load report
    // asks either half what it declares without building anything at all.
    const declSet = (half: (scope: FlaggedScope, decls: PropertyDecl[]) => PropertyDecl[]): DeclSet => ({
      story: half("story", bundle.story.properties),
      box: new Map(bundle.boxes.map((box) => [box.id, half("box", box.properties)])),
      deck: new Map(bundle.boxes.flatMap((box) => box.decks.map(
        (deck): [string, PropertyDecl[]] => [deck.id, half("deck", deck.properties)]))),
      hand: new Map(bundle.boxes.flatMap((box) => box.hands.map(
        (hand): [string, PropertyDecl[]] => [hand.id, half("hand", handDeclsOf(internals, hand))]))),
      value: new Map(allTagGroups(bundle).flatMap((group) => group.tags.map(
        (tag): [string, PropertyDecl[]] => [tag.id, half("value", tag.properties ?? [])]))),
    });
    internals.flowDecls = declSet(flowHalf);
    internals.sharedDecls = declSet(sharedHalf);
    internals.durable.shared = durableProps(internals, internals.sharedDecls);
    internals.durable.flow = durableProps(internals, internals.flowDecls);

    this.initShared(opts.hostScopes ?? {}, setup);
    // The shared bags claimed values the game may have loaded first.
    internals.reportRefits();
    // Last, so the game's handler and the decision log hear about an engine
    // that is ready to be asked.
    for (const s of setup) this.reportSetup(s.node, s.message);
  }

  /** Report a set-up problem (a `setup` PlayError, which belongs to no flow):
   *  to the game's onError, and to the decision log. */
  private reportSetup(node: string, message: string): void {
    this.internals.onError({ flow: "", kind: "setup", node, message });
    if (this.internals.logCap !== undefined || this.internals.engineTracing()) {
      this.internals.emitEngine("", { type: "diagnostic", where: "setup", message });
    }
  }

  /** Build the shared stores, register them and the host scopes, and set up
   *  each host scope's seam. Once, for the engine's life: reset and loads
   *  reseed the bags in place, so the registry never sees them come and go. */
  private initShared(bound: Record<string, HostScope>, setup: Array<{ node: string; message: string }>): void {
    const internals = this.internals;
    const reg = internals.registry;
    const declared = bundleHostScopes(internals.bundle);
    internals.shared = buildPartition(internals, internals.sharedDecls);
    const registered: string[] = [];
    // Register now, and remember how, so a failed hotSwap can register again.
    const mount = (key: string, register: () => void): void => {
      register();
      registered.push(key);
      this.sharedMounts.push({ key, mount: register });
    };
    try {
      // `story` is this engine's token whether or not the bundle declares a shared
      // @story property: registering it is what makes a clash show at once.
      const story = internals.shared.story;
      mount("story", () => { reg.mountOwned("story", story, { owner: OWNER }); }); // claims values the game loaded first
      for (const kind of OWNED_SCOPES) {
        for (const [id, bag] of internals.shared[kind]) {
          if (bag.declarations().length === 0) continue; // holds nothing: not registered
          mount(sharedKey(kind, id), () => { reg.mountOwned(sharedKey(kind, id), bag, { owner: OWNER }); });
        }
      }
      // Each scope the game binds is EXTERNAL: the game keeps the values, the
      // registry never saves them. Its declarations (types, read-only) come
      // from the bundle.
      for (const [token, resolver] of Object.entries(bound)) {
        if (resolver === undefined) continue;
        const spec = declared.find((s) => s.token === token);
        // Tokens are case-sensitive everywhere, so a near miss is reported,
        // never folded: folding would make a second spelling of the scope.
        // Another engine's scope the content names is that engine's to declare.
        if (spec === undefined && !(internals.bundle.externalScopes ?? []).includes(token)) {
          const near = declared.find((s) => s.token.toLowerCase() === token.toLowerCase());
          setup.push({ node: token, message: near
            ? `hostScopes binds "${token}", which this bundle doesn't declare: it declares "${near.token}", and a scope's token is case-sensitive`
            : `hostScopes binds "${token}", which this bundle doesn't declare` });
        }
        const decls = spec?.declarations?.map(toForeignDecl);
        mount(token, () => { reg.defineForeign(token, resolver, decls, { normalise: identity, writable: spec?.writable ?? true, owner: OWNER }); });
      }
      // A declared host scope nobody bound. A standalone engine is its own
      // game, so it self-backs the scope from its declared defaults,
      // DECLARATIONS AND ALL, as a property the registry stores and SAVES (only
      // a resolver the game binds is external). The bag keeps `writable: false`
      // so an examiner still reads it there, and the kernel lets a
      // `{ host: true }` write past it, which is what the game's own surface
      // passes. Given the GAME's registry, the engine registers nothing here:
      // those scopes are the game's to register.
      for (const spec of internals.ownsRegistry ? declared : []) {
        const token = spec.token;
        if (bound[token] !== undefined || reg.has(token)) continue;
        reg.defineOwned(token, selfBackedDecls(spec), { normalise: identity, pathPrefix: `${token}.`, owner: OWNER });
        const bag = reg.ownedBag(token);
        registered.push(token);
        this.sharedMounts.push({ key: token, mount: () => { reg.mountOwned(token, bag, { owner: OWNER }); } });
      }
    } catch (e) {
      for (const k of registered) reg.remove(k, { keep: true }); // a clash leaves the game's registry as it was
      throw e;
    }
    internals.hostScopes = new Map();
    internals.hostResolvers = {};
    for (const spec of declared) {
      const token = spec.token;
      const resolver = bound[token];
      const isBound = resolver !== undefined;
      const state: HostScopeState = {
        spec,
        resolver: {
          get: (n) => reg.get(token, n),
          set: (n, v) => { reg.set(token, n, v); },
        },
        // A bound resolver is opaque: it keeps whatever rule the game has, so
        // the story's `writable: false` is kept by `hostReadOnly` before this seam.
        ...(isBound
          ? resolver!.set !== undefined ? { set: (n: string, v: ScalarValue): void => { reg.set(token, n, v, { host: true }); } } : {}
          : { set: (n: string, v: ScalarValue, host?: boolean): void => { reg.set(token, n, v, host === true ? { host: true } : undefined); } }),
        selfBacked: !isBound && this.sharedMounts.some((m) => m.key === token),
      };
      internals.hostScopes.set(token, state);
      internals.hostResolvers[token] = state.resolver;
    }
  }

  /** Every shared bag back to its declared defaults, in place (the registry
   *  keeps them registered), the self-backed host scopes included. */
  private reseedShared(): void {
    const { shared, sharedDecls, registry } = this.internals;
    shared.story.reseed(sharedDecls.story as unknown as ScopeDeclaration[]);
    for (const kind of OWNED_SCOPES) {
      for (const [id, bag] of shared[kind]) bag.reseed((sharedDecls[kind].get(id) ?? []) as unknown as ScopeDeclaration[]);
    }
    for (const host of this.internals.hostScopes.values()) {
      if (host.selfBacked) registry.reseedOwned(host.spec.token, selfBackedDecls(host.spec));
    }
  }

  /** Quality ladders by scope for the eval channel (design/quality.md):
   *  host scopes by token then name; story by name; box/deck/value keyed by
   *  owner id then name.
   *  Built once - a bundle's declarations never change. Ladders are
   *  declaration-level, so the sharing flag does not touch them. */
  private initLadders(): void {
    const internals = this.internals;
    const grab = (decls: readonly (PropertyDecl | HostScopeDecl)[] | undefined): Map<string, readonly string[]> => {
      const m = new Map<string, readonly string[]>();
      for (const d of decls ?? []) if (d.type === "quality" && d.stages !== undefined) m.set(d.name, d.stages);
      return m;
    };
    const b = internals.bundle;
    for (const spec of bundleHostScopes(b)) internals.ladders.host.set(spec.token, grab(spec.declarations));
    internals.ladders.story = grab(b.story.properties);
    for (const box of b.boxes) {
      internals.ladders.box.set(box.id, grab(box.properties));
      for (const deck of box.decks) internals.ladders.deck.set(deck.id, grab(deck.properties));
      for (const group of box.tagGroups) {
        for (const tag of group.tags) internals.ladders.value.set(tag.id, grab(tag.properties));
      }
      for (const hand of box.hands) internals.ladders.hand.set(hand.id, grab(handDeclsOf(internals, hand)));
    }
    // The zones, once, keyed by tag id like every other tag (3.3).
    for (const tag of b.map?.group.tags ?? []) internals.ladders.value.set(tag.id, grab(tag.properties));
    const any = (m: Map<string, Map<string, readonly string[]>>): boolean =>
      [...m.values()].some((x) => x.size > 0);
    internals.hasQualities = [...internals.ladders.host.values()].some((x) => x.size > 0) || internals.ladders.story.size > 0
      || any(internals.ladders.box) || any(internals.ladders.deck)
      || any(internals.ladders.value) || any(internals.ladders.hand);
  }

  /** The registry this engine's bags live in: the game's, the very object the
   *  `registry` option passed in, or the one the engine made because it was
   *  given none. The same object for the engine's life. Read-only: a game
   *  hands an engine its registry at construction and never swaps it. */
  get registry(): ScopeRegistry {
    return this.internals.registry;
  }

  // --- flow management (Patter's surface, name for name) ----------------------

  /** Open (or REPLACE) the named flow. An existing id's flow is closed
   *  first - re-opening a name is a reset of that name's whole per-flow
   *  state; shared state is untouched. There is no default flow: "main" is
   *  a caller convention, not an engine rule. */
  openFlow(id: string, opts: OpenFlowOptions = {}): Flow {
    return this.open(id, opts, false);
  }

  /** openFlow, and loadGame's rebuild. `claim` says the new flow's bags take
   *  the values the registry holds for them (a load); a fresh open is a reset
   *  of that name, so anything waiting for it is discarded first. */
  private open(id: string, opts: OpenFlowOptions, claim: boolean): Flow {
    if (opts.durable !== undefined && opts.restore !== undefined) {
      throw new StoryletError(`openFlow "${id}": restore and durable cannot be given together; a restore already carries the flow's durable state`);
    }
    // A pocket is checked and planned before the name is touched: a refusal,
    // and anything the plan finds, must leave the flow already open as it was.
    let durable: { plan: DurablePlan; draft: ReportDraft; content: BundleContent } | undefined;
    if (opts.durable !== undefined) {
      assertDurable(this.internals, opts.durable);
      const draft = emptyDraft();
      const { flow: props, flowCards } = this.internals.durable;
      durable = { plan: planDurable(this.internals, props, flowCards, opts.durable, id, draft), draft, content: opts.durable.content };
    }
    this.assertExternalScopes();
    // The world's claims as they stand WITHOUT this name, taken before the
    // replace: a resume competes with the other flows, never with the flow it
    // is replacing (which is about to release everything it holds).
    const otherClaims = opts.restore !== undefined ? this.sharedClaims(id) : undefined;
    // Replacing an existing id KEEPS its place in the order. `close()` would
    // drop the key, and a JS Map re-inserts a deleted key at the END, so
    // openFlow("a"); openFlow("b"); openFlow("a") listed [b, a] here and
    // [a, b] on all three ports - a different `flows()` order and, since
    // `saveGame` keys its flows in that order, a different `.storyletsave`
    // byte stream for the same run (2026-08-29). markClosed without dropFlow
    // is the difference: the old handle goes inert, the slot stays put. The
    // registry cannot keep a key's place the same way (the old flow's keys are
    // removed and the new flow's appended), so `saveGame` writes the registry
    // section in flow order itself: see `registrySection` (2026-10-01).
    const existing = this.flowsById.get(id);
    if (existing) {
      // Say so BEFORE the old flow goes inert, while its board is still readable.
      const dealt = existing.heldCardIds().length;
      if (dealt > 0) this.onReplacedFlow?.(id, dealt);
      existing.markClosed();
    }
    if (!claim) this.internals.registry.discardParked(flowPrefix(id));
    const flow = new Flow(this, this.internals, id, opts.seed ?? this.seed);
    this.flowsById.set(id, flow);
    if (opts.restore !== undefined) {
      const draft = emptyDraft();
      // Cloned: a caller holding one blob may resume two flows from it, and
      // an aliased playLog would then grow in both.
      const clean = this.planFlowRestore(id, structuredClone(opts.restore), otherClaims, draft);
      flow.restore(clean);
      const content = this.internals.bundle.content;
      opts.onRestoreReport?.(finishReport(content, content, [id], draft));
    }
    if (durable !== undefined) {
      flow.writeDurable(durable.plan);
      opts.onRestoreReport?.(finishReport(this.internals.bundle.content, durable.content, [id], durable.draft));
    }
    return flow;
  }

  getFlow(id: string): Flow | undefined {
    return this.flowsById.get(id);
  }

  /** Every live flow, open order. */
  flows(): Flow[] {
    return [...this.flowsById.values()];
  }

  /** Close the named flow: its handle goes INERT (every verb throws). A
   *  dropped-but-held flow must not keep writing shared state (Patter's
   *  stale-handle lesson). Unknown ids are a quiet no-op, like closing a
   *  closed door. */
  closeFlow(id: string): void {
    const flow = this.flowsById.get(id);
    if (!flow) return;
    this.flowsById.delete(id);
    flow.markClosed();
  }

  /** @internal - Flow.close() routes here so both doors agree. */
  dropFlow(id: string, flow: Flow): void {
    if (this.flowsById.get(id) === flow) this.flowsById.delete(id);
  }

  /** Close every flow and reseed the shared state to its defaults (the
   *  self-backed host scopes included; a host-bound one is the host's and is
   *  not touched). */
  reset(): void {
    this.dropRun(false);
    this.reseedShared();
    // Values loaded for bags nobody has claimed yet are the old run's too: a
    // flow opened after the reset must not pick them up. Other engines' stay.
    this.internals.registry.discardParked("storylets/");
  }

  /** End the run: clear the log, close every flow, forget spent cards. Each
   *  flow's bags leave the registry; `keepFlows` names the flows whose values
   *  are kept there for the flow that replaces them (a load into the game's
   *  registry). */
  private dropRun(keepFlows: Set<string> | false): void {
    // The log is a run-lifetime utility and is not saved; a reset is a new run.
    // All three ports cleared it here and the reference did not, so `reset()`
    // (and `loadGame`) left the previous run's entries in place with `seq`
    // continuing across the boundary - while the same call on Godot, Unity or
    // Unreal returned an empty log (2026-08-29).
    this.engineLog = [];
    for (const [id, flow] of this.flowsById) {
      flow.releaseBags(keepFlows !== false && keepFlows.has(id));
      flow.markClosed();
    }
    this.flowsById.clear();
    this.spent.clear();
  }

  // --- shared scarcity (design/shared-scarcity.md) -----------------------------

  /** Cards a shared `redraw: "never"` has taken out of the world, by card id.
   *  The claim ledger is DERIVED from live boards and so needs no storage;
   *  this one is durable, so it rides the save's shared half. */
  private spent = new Set<string>();

  /** @internal */
  isTaken(cardId: string): boolean {
    return this.spent.has(cardId);
  }

  /** @internal - take a shared one-shot out of the world, as its play does.
   *  Keyed by internal id. A host carries spends across a run with
   *  `saveDurable` and `loadDurable`, which speak gameIds and check that the
   *  card is still one that may be carried (ruling H). */
  markTaken(cardId: string): void {
    this.spent.add(cardId);
  }

  // --- the run's log (design/shared-scarcity.md 8.2) ---------------------------

  /** Every flow's events in one ordered stream, each tagged with its flow.
   *  Opt in with the same `log` option the flow logs use; capped the same way.
   *
   *  This exists because a flow's own log cannot answer the question a run
   *  raises: when a story action in ANOTHER flow moves shared state, your
   *  flow's log says nothing and your value simply changes. Reading a run
   *  needs one stream that says who did what, and merging the per-flow logs by
   *  hand is a thing every host would otherwise have to write. */
  private engineLog: EngineLogEntry[] = [];
  private engineSeq = 0;

  log(): readonly EngineLogEntry[] {
    return this.engineLog;
  }

  clearLog(): void {
    this.engineLog = [];
  }

  /** @internal - shared claims across every LIVE flow, card id -> holders.
   *  Derived, which is what makes closeFlow and the openFlow replace release
   *  what a flow was holding: its board leaves the map with it. `except`
   *  leaves one flow out: what the REST of the world holds, which is the
   *  question a resume under that name has to ask. */
  sharedClaims(except?: string): Map<string, number> {
    const counts = new Map<string, number>();
    for (const [flowId, flow] of this.flowsById) {
      if (flowId === except) continue;
      for (const cardId of flow.heldCardIds()) counts.set(cardId, (counts.get(cardId) ?? 0) + 1);
    }
    return counts;
  }

  // --- engine-level state access ----------------------------------------------

  /**
   * Read shared state by path: "world.x", "story.gold" (when shared),
   * "box.village.heat" (when shared) - the owner segment is its GAMEID
   * (design/engine-server.md 4.4). A ref that resolves PER-FLOW throws,
   * naming the fix - silently answering with some flow's copy (or a junk
   * default) was the bug Patter's engine.getProperty guard exists to stop.
   */
  getProperty(path: string): ScalarValue {
    const found = this.resolveShared(propertyPath(path));
    const value = found.kind === "host" ? found.host.resolver.get(found.name)
      : found.kind === "scope" ? this.internals.registry.get(found.token, found.name)
      : found.bag.get(found.name);
    if (value === undefined) throw new StoryletError(`no property at "${path}"`);
    return value;
  }

  setProperty(path: string, value: ScalarValue): void {
    const found = this.resolveShared(propertyPath(path));
    checkHostWrite(this.internals, path, value);
    if (found.kind === "host") {
      if (!found.host.set) {
        throw new StoryletError(`@${found.host.spec.token} is read-only here: the host bound no write`);
      }
      found.host.set(found.name, value, true);
      return;
    }
    if (found.kind === "scope") {
      this.internals.registry.set(found.token, found.name, value, { host: true });
      return;
    }
    // A host write: silent under the firing rule (no subscriber feedback
    // loop), but visible to the bag's audit hook - and a HOST write, so a
    // `writable: false` does not refuse it. That flag is the story's promise
    // about its own outcomes; this is the game speaking, in any scope.
    found.bag.set(found.name, value, { silent: true, reason: "host setProperty", host: true });
  }

  private resolveShared(path: string):
    | { kind: "host"; host: HostScopeState; name: string }
    | { kind: "scope"; token: string; name: string }
    | { kind: "bag"; bag: StateBag; name: string } {
    const parts = path.split(".");
    const perFlow = (): never => {
      throw new StoryletError(`"${path}" is per-flow state - read it on a Flow, not the Engine`);
    };
    const host = parts.length === 2 ? this.internals.hostScopes.get(parts[0]!) : undefined;
    if (host !== undefined) return { kind: "host", host, name: parts[1]! };
    // Another engine's game-wide scope (`patter.gold`): every engine reads every scope.
    if (parts.length === 2 && parts[0] !== "story" && this.internals.registry.has(parts[0]!)) {
      return { kind: "scope", token: parts[0]!, name: parts[1]! };
    }
    if (parts.length === 2 && parts[0] === "story") {
      const name = parts[1]!;
      if (ownValue(this.internals.shared.story, name) !== undefined) return { kind: "bag", bag: this.internals.shared.story, name };
      if (this.internals.flowDecls.story.some((d) => d.name === name)) perFlow();
      throw new StoryletError(`no property at "${path}"`);
    }
    if (parts.length === 3 && isOwnedScope(parts[0])) {
      const kind = parts[0];
      const [, segment, name] = parts as unknown as [string, string, string];
      const owner = ownerOrThrow(this.internals, kind, segment, name);
      if (owner.legacy) this.diagnose(legacyAddressMessage(this.internals, kind, segment, name));
      const id = owner.id;
      const bag = this.internals.shared[kind].get(id);
      if (bag !== undefined && ownValue(bag, name) !== undefined) return { kind: "bag", bag, name };
      if (this.internals.flowDecls[kind].get(id)?.some((d) => d.name === name)) perFlow();
      if (bag === undefined && !this.internals.flowDecls[kind].has(id)) throw new StoryletError(`no ${kind} store "${segment}"`);
      throw new StoryletError(`no property at "${path}"`);
    }
    throw new StoryletError(`bad property path "${path}"`);
  }

  /** The engine's own surface has no flow, so an engine-level diagnostic
   *  carries the empty flow id - the same way a LoadReport's shared half
   *  carries no flow. It reaches the run log and the engine tap; there is
   *  nowhere else for it to go, and it fires only on a legacy address. */
  private diagnose(message: string): void {
    this.internals.emitEngine("", { type: "diagnostic", where: "property address", message });
  }

  /** Content that names another engine's scope (`@patter.visits`) runs only
   *  where that engine is on this registry: without it every read would answer
   *  false and every write fail, so the flow is refused as it opens, before
   *  anything changes. By then a game has built all of its engines, whatever
   *  order it built them in. The same message on every runtime. */
  private assertExternalScopes(): void {
    for (const token of this.internals.bundle.externalScopes ?? []) {
      if (!this.internals.registry.has(token)) {
        throw new StoryletError(`this content names @${token}, which no engine on this registry registered: `
          + `give every engine the game's one registry`);
      }
    }
  }

  /** The shared surface as examiner rows: the host scopes (read through
   *  their resolvers) then the shared partitions. Per-flow rows live on each Flow. */
  listProperties(): PropertyRow[] {
    const out: PropertyRow[] = hostRows(this.internals);
    // The bag composes its rows' addresses from its own pathPrefix, so the prefix here is
    // only the caller's label for the mount; the row arrives already addressed.
    const add = (_prefix: string, bag: StateBag): void => {
      for (const row of bag.rows()) out.push(row);
    };
    add("story", this.internals.shared.story);
    for (const kind of OWNED_SCOPES) {
      for (const [id, bag] of this.internals.shared[kind]) add(addressOf(this.internals, kind, id), bag);
    }
    return out;
  }

  /** The SHARED kernel bags with their store path prefixes (the state
   *  logger's mount surface). The @world container is the host's own bag -
   *  the host mounts it itself. */
  listBags(): BagMount[] {
    const mounts: BagMount[] = [{ prefix: "story", bag: this.internals.shared.story }];
    for (const kind of OWNED_SCOPES) {
      for (const [id, bag] of this.internals.shared[kind]) mounts.push({ prefix: addressOf(this.internals, kind, id), bag });
    }
    return mounts;
  }

  /** Every flow's trace, one stream, each event tagged with its flow id. */
  subscribeTrace(handler: EngineTraceHandler): () => void {
    // A Set: the same handler subscribed twice is registered once (ruling F).
    this.engineTraceHandlers.add(handler);
    return () => { this.engineTraceHandlers.delete(handler); };
  }

  // --- persistence (schema 4) -------------------------------------------------

  /**
   * Live bundle refresh: rebuild on an edited bundle with the whole run carried
   * over, and return the replacement with the report its load produced.
   *
   * Standalone, that is a save and a load into a new engine, and this one is left
   * untouched (discard it). With the game's registry the two cannot both hold the
   * same keys, so this engine is spent afterwards (its flows closed, the
   * replacement holding everything on the same registry): it carries its
   * own values into the snapshot, steps out of the registry, and the replacement
   * loads them the way a standalone save loads: so the report covers the
   * properties the edit dropped, defaulted, or retyped, and a dropped property
   * is dropped rather than kept. Values the game loaded that were still waiting
   * for a flow of this engine carry across as they were, and nothing belonging
   * to any other engine is touched. A save for another project is refused before
   * anything moves; if the rebuild fails for any other reason, this engine takes
   * its registrations back and is left exactly as it was.
   */
  hotSwap(bundle: Bundle, opts: EngineOptions = {}): { engine: Engine; report: LoadReport } {
    if (bundle.content.project !== this.internals.bundle.content.project) {
      // Names the BUNDLE: there is no save in a hot swap, so the load's own
      // "save is for project" wording pointed the reader at the wrong thing.
      throw new StoryletError(`hotSwap: the bundle is for project "${bundle.content.project}", this engine runs "${this.internals.bundle.content.project}"`);
    }
    const options: EngineOptions = { ...this.creationOptions, ...opts };
    const snapshot = this.saveGame();
    const reg = this.internals.registry;
    if (this.internals.ownsRegistry) {
      // Its own registry: a save and a load into a new engine, as it always was,
      // and this engine is left untouched.
      const next = new Engine(bundle, { ...options, registry: undefined });
      return { engine: next, report: next.loadGame(snapshot) };
    }
    // This engine's own values, registered or still waiting for a flow.
    const mine: Record<string, Record<string, ScalarValue>> = {};
    for (const [key, values] of Object.entries(reg.save())) {
      if (key === "story" || key.startsWith("storylets/")) mine[key] = values;
    }
    const registeredKeys = new Set([
      ...this.sharedMounts.map((m) => m.key),
      ...[...this.flowsById.values()].flatMap((f) => f.registeredKeys()),
    ]);
    const waiting = Object.fromEntries(Object.entries(mine).filter(([key]) => !registeredKeys.has(key)));
    // Step out of the registry. The bags keep their values, so stepping back in is exact.
    for (const { key } of this.sharedMounts) if (reg.has(key)) reg.remove(key);
    for (const flow of this.flowsById.values()) flow.releaseBags(false);
    let next: Engine | undefined;
    try {
      next = new Engine(bundle, { ...options, registry: reg });
      const report = next.loadGame({ ...snapshot, registry: mine });
      // Values that were waiting for a flow wait on, for the replacement's flow of that name.
      const stillWaiting = Object.fromEntries(Object.entries(waiting).filter(([key]) => !reg.has(key)));
      if (Object.keys(stillWaiting).length > 0) reg.load(stillWaiting, { keepParked: true });
      this.dropRun(false);
      return { engine: next, report };
    } catch (e) {
      // Back as it was. The replacement's registrations go; a constructor that
      // failed part way parked what it had claimed, so this engine's keys are
      // cleared of anything waiting, registered again, and its own values laid
      // back over them (the waiting ones parked again).
      next?.dropRun(false);
      for (const { key } of next?.sharedMounts ?? []) if (reg.has(key)) reg.remove(key);
      reg.discardParked("storylets/");
      for (const { mount } of this.sharedMounts) mount();
      for (const flow of this.flowsById.values()) flow.mountBags();
      // `story` may have claimed what the failed replacement parked there: back to
      // its declarations, then this engine's own values over it.
      this.reseedShared();
      reg.load(mine, { keepParked: true });
      throw e;
    }
  }

  /** The whole engine's NON-property state, one envelope: the spent cards
   *  once, then every live flow (board, clocks, cooldowns, PRNG, play log)
   *  keyed by its id. The property values are the registry's: a standalone
   *  engine (one that made its own registry) carries them here under
   *  `registry`, self-backed host scopes included; a game that passed a registry
   *  saves it once itself, beside each engine's envelope. */
  saveGame(): SaveEnvelope {
    return structuredClone({
      schema: SAVE_SCHEMA,
      content: this.internals.bundle.content,
      ...(this.internals.ownsRegistry ? { registry: this.registrySection() } : {}),
      shared: { spent: [...this.spent].map((id) => cardKey(this.internals, id)).sort() },
      flows: Object.fromEntries([...this.flowsById].map(([id, flow]) => [id, flow.snapshot(false)])),
    });
  }

  /** The registry's values in CANONICAL order, the order a load rebuilds them
   *  in: the engine-wide keys as the constructor registered them, then each
   *  flow's keys in the order the save's `flows` lists the flows (each flow's
   *  own registration order), then
   *  anything else the registry holds (values still waiting for a key), as the
   *  registry lists it. The registry itself lists keys in registration order,
   *  and a flow replaced in place (`open()` above keeps its slot in
   *  `flowsById`) re-registers its keys at the END, so `openFlow("a");
   *  openFlow("b"); openFlow("a")` saved b's keys before a's while a load
   *  rebuilt a's first: the same run, different `.storyletsave` bytes, and a
   *  save loaded and saved again no longer equal to itself. It is the
   *  2026-08-29 rule carried into the section save@2 moved the per-flow values
   *  to (2026-10-01). Order does not matter on READ (`partitionsFromSections`
   *  sorts by key shape), so a save written in the old order loads as before.
   *
   *  "The order the save lists them" is JS OBJECT order (ruling E,
   *  2026-10-06), which is not always `flows()` order: a flow id that looks like
   *  an integer ("7") comes first, ascending, in any JS object, and a load
   *  reopens the flows in the order the parsed save hands them over. Without
   *  this, `openFlow("main"); openFlow("7")` saved main's keys first and the
   *  loaded engine saved 7's first. */
  private registrySection(): Sections {
    const all = this.internals.registry.save() as Sections;
    const out: Sections = {};
    const take = (key: string): void => {
      if (Object.prototype.hasOwnProperty.call(all, key) && !Object.prototype.hasOwnProperty.call(out, key)) out[key] = all[key]!;
    };
    for (const { key } of this.sharedMounts) take(key);
    for (const flow of Object.values(Object.fromEntries(this.flowsById))) for (const key of flow.registeredKeys()) take(key);
    for (const key of Object.keys(all)) take(key);
    return out;
  }

  /** ONE flow's blob, to park a visit that is walking away: the same shape
   *  the envelope carries per flow, and the same shape `openFlow`'s `restore`
   *  option takes back (design/engine-server.md 4.1). Saving the whole
   *  envelope to park one of four hundred players is wrong in cost and in
   *  meaning. Throws for a name that is not open - a closed flow has nothing
   *  left to save. */
  saveFlow(id: string): FlowSave {
    const flow = this.flowsById.get(id);
    if (!flow) throw new StoryletError(`unknown flow "${id}"`);
    // Parked whole, properties included: a parked flow's bags leave the
    // registry when it closes, so its values have to travel with it.
    return structuredClone(flow.snapshot(true));
  }

  /**
   * The engine's DURABLE HALF, the installation's memory (ruling H): every
   * shared `durable` property's value by its address, and every shared
   * durable one-shot that has been spent, by card gameId. What a new run
   * starts from, through `loadDurable`; a flow's own half is
   * `flow.saveDurable()`.
   */
  saveDurable(): DurableSave {
    const { bundle, shared, durable } = this.internals;
    return structuredClone({
      schema: DURABLE_SCHEMA,
      content: bundle.content,
      values: durableValues(shared, durable.shared),
      spent: durable.sharedCards.filter((e) => this.spent.has(e.card.id)).map((e) => effectiveGameId(e.card)).sort(),
    });
  }

  /**
   * Write the engine's durable half into this engine, a fresh one at the top
   * of a new run (ruling H). Every shared durable property takes the memory's
   * value, or its default where the memory carries none that fits, and every
   * spend the memory lists is taken out of the world. Nothing else is touched:
   * not a run-scoped value, not a flow, not a spend the memory does not name.
   *
   * Returns the report `loadGame` would give, for this half: an address this
   * build does not declare durable and shared is dropped, a value its
   * declaration no longer takes is retyped, a durable declaration the memory
   * lacks is defaulted, and a spend for a card that is no longer a shared
   * durable one-shot is a dropped spent card. Another project's memory, or an
   * unknown schema, is refused before anything moves.
   */
  loadDurable(memory: DurableSave): LoadReport {
    const internals = this.internals;
    assertDurable(internals, memory);
    const draft = emptyDraft();
    const plan = planDurable(internals, internals.durable.shared, internals.durable.sharedCards, memory, undefined, draft);
    for (const prop of internals.durable.shared) {
      const value = plan.values.has(prop)
        ? plan.values.get(prop)!
        : defaultFor(prop.decl as unknown as ScopeDeclaration);
      putDurable(durableBag(internals.shared, prop), prop.decl.name, value);
    }
    for (const cardId of plan.cards) this.spent.add(cardId);
    return finishReport(internals.bundle.content, memory.content, [], draft);
  }

  /** What `loadGame(envelope)` would do that is not a plain restore, without
   *  doing any of it (design/engine-server.md 4.9). Pure: nothing on this
   *  engine moves. A project mismatch is refused here exactly as `loadGame`
   *  refuses it - it is the one thing neither call will tolerate. */
  previewLoad(envelope: SaveEnvelope | SaveEnvelopeV1): LoadReport {
    refuseUnreadableSave(envelope);
    this.assertSameProject(envelope);
    const plan = this.planLoad(envelope);
    if (plan.sections === undefined) return plan.report;
    // The held cards are checked against the values the envelope carries,
    // which only a load puts in place: so the load runs on a scratch engine of
    // its own, with its own registry, and this one is left as it was.
    const { seed, hostScopes } = this.creationOptions;
    const scratch = new Engine(this.internals.bundle, {
      ...(seed !== undefined ? { seed } : {}), ...(hostScopes !== undefined ? { hostScopes } : {}), onError: () => {},
    });
    try {
      return scratch.loadGame(envelope);
    } catch {
      return plan.report;
    }
  }

  /** What `openFlow(id, { restore: saved })` would do to a flow of that name,
   *  without doing it: the same report shape, since a visit parked under one
   *  build and resumed under the next raises the same questions. Pure. */
  previewFlowRestore(id: string, saved: FlowSave): LoadReport {
    const draft = emptyDraft();
    this.planFlowRestore(id, saved, this.sharedClaims(id), draft);
    const content = this.internals.bundle.content;
    return finishReport(content, content, [id], draft);
  }

  /** Restore: shared state once, then every flow REBUILT from its blob.
   *  Handles held from before the load are closed and inert (Patter's
   *  rule); take fresh ones from getFlow()/flows().
   *
   *  Property values come from the registry. An envelope that carries them
   *  (a standalone engine's, or a version 1 envelope) has them walked, cleaned,
   *  and moved into the registry here, over fresh defaults. Otherwise the game
   *  loads its registry itself, before or after this call: each flow's bags
   *  are handed back to the registry with their values, and the restored
   *  flows claim them. The report then covers only what this envelope holds;
   *  the registry's own load rule applies to the values.
   *
   *  Returns the report `previewLoad` would have given for this envelope: the
   *  drift tolerance that makes a load forgiving is what hides its cost, so
   *  the cost comes back with the load whether or not anybody looked first. */
  loadGame(envelope: SaveEnvelope | SaveEnvelopeV1): LoadReport {
    try {
      return this.loadGameInto(envelope);
    } finally {
      // The game's own refitting load of its registry lands before this one.
      this.internals.reportRefits();
    }
  }

  private loadGameInto(envelope: SaveEnvelope | SaveEnvelopeV1): LoadReport {
    refuseUnreadableSave(envelope);
    this.assertSameProject(envelope);
    this.assertExternalScopes();
    const plan = this.planLoad(structuredClone(envelope));
    const reg = this.internals.registry;
    if (plan.sections !== undefined) {
      // The envelope carries the values: every bag of this engine back to its
      // defaults, then the cleaned values over them.
      this.reset();
      // The engine's own registry takes the save wholesale. A game's registry may
      // hold values the game loaded for other engines, still waiting: add to
      // those, never replace them.
      if (this.internals.ownsRegistry) reg.load(plan.sections);
      else reg.load(plan.sections, { keepParked: true });
    } else {
      this.dropRun(new Set(plan.flows.map(([id]) => id)));
    }
    for (const id of plan.spent) this.spent.add(id);
    for (const [id, clean] of plan.flows) this.open(id, {}, true).restore(clean);
    if (plan.sections === undefined) return plan.report;
    // With the values in place, every held card is checked as a deal checks
    // it, and what no longer qualifies goes, in the report (the hands-on
    // review, 2026-10, foundation 7). A game that loads its own registry has
    // no values here yet: its next deal does the same.
    const dropped = plan.flows.flatMap(([id]) =>
      (this.flowsById.get(id)?.dropUnqualified() ?? []).map((e) => ({ flow: id, ...e })));
    if (dropped.length === 0) return plan.report;
    return {
      ...plan.report,
      exact: false,
      evicted: byKey([...plan.report.evicted, ...dropped], (e) => [e.flow, e.hand, e.card, e.reason].join(SORT_SEP)),
    };
  }

  private assertSameProject(envelope: SaveEnvelope | SaveEnvelopeV1): void {
    if (envelope.content.project !== this.internals.bundle.content.project) {
      throw new StoryletError(`save is for project "${envelope.content.project}", bundle is "${this.internals.bundle.content.project}"`);
    }
  }

  /** The whole-envelope walk: the report, and the cleaned state the apply
   *  half writes. Nothing here touches the engine, which is what lets
   *  previewLoad and loadGame share it. */
  private planLoad(envelope: SaveEnvelope | SaveEnvelopeV1): {
    report: LoadReport;
    /** The cleaned property values to move into the registry, when the
     *  envelope carries any. */
    sections?: Sections;
    spent: string[];
    flows: [string, FlowSave][];
  } {
    const schema = (envelope as { schema?: unknown }).schema;
    if (schema !== SAVE_SCHEMA && schema !== SAVE_SCHEMA_V2 && schema !== SAVE_SCHEMA_V1) throw new StoryletError(`unsupported save schema: ${String(schema)}`);
    const draft = emptyDraft();
    // Where the property values are, if this envelope has them: a version 1
    // envelope's partitions, or a standalone engine's registry sections.
    const flowIds = new Set(Object.keys(envelope.flows ?? {}));
    let moved: { shared: PropsPartition; flows: Map<string, PropsPartition>; rest: Sections } | undefined;
    if (envelope.schema === SAVE_SCHEMA_V1) {
      moved = {
        shared: envelope.shared?.props ?? emptyPartitionValues(),
        flows: new Map(Object.entries(envelope.flows ?? {}).map(([id, f]) => [id, f.props ?? emptyPartitionValues()])),
        rest: {},
      };
    } else if (envelope.registry !== undefined) {
      moved = partitionsFromSections(envelope.registry, flowIds);
    }
    const shared = moved !== undefined
      ? walkPartition(this.internals, this.internals.sharedDecls, moved.shared, undefined, draft)
      : undefined;
    const spent: string[] = [];
    for (const key of envelope.shared?.spent ?? []) {
      const entry = savedCard(this.internals, key);
      if (entry !== undefined) spent.push(entry.card.id);
      else draft.droppedSpent.push(key);
    }
    const flows: [string, FlowSave][] = [];
    const sections: Sections | undefined = moved !== undefined ? { ...moved.rest } : undefined;
    if (sections !== undefined && shared !== undefined) {
      sectionsOf(shared, (kind, id) => (kind === "story" ? "story" : sharedKey(kind, id!)), sections);
    }
    for (const [id, saved] of Object.entries(envelope.flows ?? {})) {
      const withProps: FlowSave = moved !== undefined
        ? { ...saved, props: moved.flows.get(id) ?? emptyPartitionValues() }
        : { ...saved };
      const clean = this.planFlowRestore(id, withProps, undefined, draft);
      if (sections !== undefined && clean.props !== undefined) {
        // The flow's values go into the registry, where its bags claim them; the
        // restored flow itself carries none.
        sectionsOf(clean.props, (kind, owner) => flowKey(id, kind, owner), sections);
        delete clean.props;
      }
      flows.push([id, clean]);
    }
    return {
      report: finishReport(this.internals.bundle.content, envelope.content, flows.map(([id]) => id), draft),
      ...(sections !== undefined ? { sections } : {}),
      spent, flows,
    };
  }

  /** One flow's walk. `otherClaims` is the rest of the world's shared ledger
   *  and is present only for a SINGLE-flow restore into a live engine: a
   *  whole-envelope load rebuilds every flow from one consistent moment, so
   *  there is nobody else to compete with. */
  private planFlowRestore(
    id: string,
    saved: FlowSave,
    otherClaims: Map<string, number> | undefined,
    draft: ReportDraft,
  ): FlowSave {
    const internals = this.internals;
    // A flow blob without properties (a version 2 envelope's: the registry has
    // them) has nothing to walk.
    const props = saved.props !== undefined ? walkPartition(internals, internals.flowDecls, saved.props, id, draft) : undefined;

    // Every key the save carries resolves to the build's internal id (see
    // savedCard); one the build no longer has is named by the key itself.
    const cooldowns: Record<string, number> = {};
    for (const [key, turn] of Object.entries(saved.cooldowns ?? {})) {
      const entry = savedCard(internals, key);
      if (entry !== undefined) cooldowns[entry.card.id] = turn;
      else draft.droppedCooldowns.push({ flow: id, card: key });
    }

    // A deleted entity has no gameId left, so it is named by the key the save
    // carries; everything the build still knows is named by its gameId.
    const cardName = (key: string): string => {
      const entry = savedCard(internals, key);
      return entry ? effectiveGameId(entry.card) : key;
    };
    const board: Record<string, string[]> = {};
    const restored = new Map<string, number>();
    for (const [handKey, keys] of Object.entries(saved.board ?? {})) {
      const known = savedHand(internals, handKey);
      if (known === undefined) {
        for (const key of keys) {
          draft.evicted.push({ flow: id, hand: handKey, card: cardName(key), reason: "hand-vanished" });
        }
        continue;
      }
      const hand = effectiveGameId(known.hand);
      const kept: string[] = [];
      for (const key of keys) {
        const entry = savedCard(internals, key);
        if (entry === undefined) {
          draft.evicted.push({ flow: id, hand, card: key, reason: "vanished" });
          continue;
        }
        const cardId = entry.card.id;
        if (otherClaims !== undefined && cardIsShared(entry.card, entry.deck.shared ?? false)) {
          const held = (otherClaims.get(cardId) ?? 0) + (restored.get(cardId) ?? 0);
          if (held >= sharedCap(entry.card)) {
            draft.evicted.push({ flow: id, hand, card: effectiveGameId(entry.card), reason: "claimed-elsewhere" });
            continue;
          }
          restored.set(cardId, (restored.get(cardId) ?? 0) + 1);
        }
        kept.push(cardId);
      }
      board[known.hand.id] = kept;
    }

    const turns: Record<string, number> = {};
    for (const [key, turn] of Object.entries(saved.turns ?? {})) {
      const box = savedBox(internals, key);
      if (box !== undefined) turns[box.id] = turn;
    }

    return {
      ...(props !== undefined ? { props } : {}),
      turns,
      prng: saved.prng,
      cooldowns,
      board,
      playLog: saved.playLog ?? [],
    };
  }
}

// --- the Flow -----------------------------------------------------------------

export class Flow {
  readonly id: string;
  private readonly engine: Engine;
  private readonly internals: Internals;
  private closed = false;

  private prng: Prng;
  /** Per-box turn counters, keyed by box id (schema 3.4) - per flow. */
  private turnCounts = new Map<string, number>();
  private cooldowns: Record<string, number> = {};
  /** The board: hand contents (card ids, dealt order), keyed by hand id. */
  private boardContents = new Map<string, string[]>();
  private playLog: PlayRecord[] = [];
  // --- play-history indexes -------------------------------------------------
  // The four play-history host functions used to SCAN playLog on every call,
  // and they are called once per candidate card per ask, so dealing was
  // O(candidates x playLog): a shipped game got measurably slower the longer
  // somebody played it, which is the failure mode nobody meets in testing.
  // Measured before the change, 2000 cards: a box with no history condition
  // held flat at 0.3ms while `count_played` went 0.8ms -> 27.9ms as the log
  // reached 4000 plays.
  //
  // These are a pure summary of `playLog`, maintained where it is appended and
  // rebuilt where it is replaced, so they cannot drift from it. They are NOT
  // saved: `playLog` is the record, this is a derivation, and rebuilding on
  // load keeps the save format untouched.
  //
  // The tag keys are the played card's OWN (groupId, tagId) pairs, which is
  // what makes the box-local rule survive: a group name resolves inside the
  // ASKING box, so a card in another box carries different ids and cannot
  // match, exactly as the per-record `inTag` check used to decide.
  private playCount = new Map<string, number>();
  private lastPlayOf = new Map<string, PlayRecord>();
  private tagPlayCount = new Map<string, number>();
  private lastPlayInTag = new Map<string, PlayRecord>();
  /** The per-flow property partitions (the not-shared halves), each bag
   *  that declares something registered under this flow's keys. */
  private stores: Partition;
  private readonly registered: string[] = [];
  /** This flow's bags that declare something, under their registry keys. */
  private readonly bagKeys: Array<[string, StateBag]> = [];

  private traceHandlers = new Set<TraceHandler>();
  private logEntries: LogEntry[] = [];
  private logSeq = 0;

  /** Merged read view per scope, built once (bags are stable for the
   *  flow's life): the flow's own bag first, the shared bag behind it.
   *  Names are disjoint (shared XOR per-flow by declaration), so "first"
   *  is routing, not shadowing. */
  private readonly storyReader: ScopeResolver;
  private readonly boxReaders = new Map<string, ScopeResolver>();
  private readonly deckReaders = new Map<string, ScopeResolver>();

  /** @internal - built by Engine.openFlow / Engine.loadGame only. */
  constructor(engine: Engine, internals: Internals, id: string, seed: number) {
    this.engine = engine;
    this.internals = internals;
    this.id = id;
    this.prng = makePrng(seed);
    this.stores = buildPartition(internals, internals.flowDecls);
    // Register the bags: each claims whatever the registry holds for its key (a
    // load); openFlow discarded that first for a fresh flow.
    const put = (key: string, bag: StateBag): void => {
      if (bag.declarations().length === 0) return; // holds nothing: not registered
      this.bagKeys.push([key, bag]);
    };
    put(flowKey(id, "story"), this.stores.story);
    for (const kind of OWNED_SCOPES) for (const [owner, bag] of this.stores[kind]) put(flowKey(id, kind, owner), bag);
    this.mountBags();
    // A bag that claimed values a refitting load parked says what it put back.
    internals.reportRefits();
    for (const box of internals.bundle.boxes) {
      this.turnCounts.set(box.id, 0);
      for (const hand of box.hands) this.boardContents.set(hand.id, []);
    }
    // Own values only (`ownValue`): a name Object.prototype carries is not a
    // property of any scope here.
    const pair = (own: StateBag | undefined, shared: StateBag | undefined): ScopeResolver => ({
      get: (n) => ownValue(own, n) ?? ownValue(shared, n),
    });
    // The shared bags are built once for the engine's life and reseeded in
    // place by loadGame/reset, so these readers never go stale.
    this.storyReader = pair(this.stores.story, internals.shared.story);
    for (const box of internals.bundle.boxes) {
      this.boxReaders.set(box.id, pair(this.stores.box.get(box.id), internals.shared.box.get(box.id)));
      for (const deck of box.decks) {
        this.deckReaders.set(deck.id, pair(this.stores.deck.get(deck.id), internals.shared.deck.get(deck.id)));
      }
    }
  }

  // --- lifetime ----------------------------------------------------------------

  get isClosed(): boolean {
    return this.closed;
  }

  /** Close this flow: the handle goes inert, every verb throws. */
  close(): void {
    if (this.closed) return;
    this.engine.dropFlow(this.id, this);
    this.markClosed();
  }

  /** @internal - the handle goes inert, its bags leave the registry, and it
   *  lets go of its trace handlers: a closed flow never emits again, and a
   *  handler that holds the flow would otherwise hold it for good on a runtime
   *  that counts references (Godot, C++). */
  markClosed(): void {
    this.releaseBags(false);
    this.traceHandlers.clear();
    this.closed = true;
  }

  /** @internal - take this flow's bags out of the registry; with `keep`, their
   *  values wait there for the flow that replaces this one (a load into the
   *  game's registry). Idempotent. */
  releaseBags(keep: boolean): void {
    for (const key of this.registered) this.internals.registry.remove(key, { keep });
    this.registered.length = 0;
  }

  /** @internal - the registry keys this flow holds right now. */
  registeredKeys(): string[] {
    return [...this.registered];
  }

  /** @internal - register this flow's bags (again): at construction, and when a
   *  failed hotSwap hands them back. Each claims what the registry holds for it. */
  mountBags(): void {
    for (const [key, bag] of this.bagKeys) {
      this.internals.registry.mountOwned(key, bag, { owner: OWNER });
      this.registered.push(key);
    }
  }

  private assertOpen(): void {
    if (this.closed) throw new StoryletError(`flow "${this.id}" is closed`);
  }

  /** A box by gameId or id, or the refusal every verb that takes one gives. */
  private resolveBox(boxRef: string): Box<Expression> {
    const box = this.internals.boxesByGameId.get(boxRef) ?? this.internals.boxesById.get(boxRef);
    if (!box) throw new StoryletError(`unknown box "${boxRef}"`);
    return box;
  }

  /** A box's current turn (schema 3.4), on THIS flow's clock. */
  turn(boxRef: string): number {
    this.assertOpen();
    return this.turnCounts.get(this.resolveBox(boxRef).id) ?? 0;
  }

  /** Subscribe to this flow's deal/play trace (schema 5). Returns the
   *  unsubscribe. With no subscribers anywhere the flow does no trace work. */
  subscribeTrace(handler: TraceHandler): () => void {
    // A Set: the same handler subscribed twice is registered once (ruling F).
    this.traceHandlers.add(handler);
    return () => { this.traceHandlers.delete(handler); };
  }

  private get tracing(): boolean {
    return this.traceHandlers.size > 0 || this.internals.logCap !== undefined || this.internals.engineTracing();
  }

  private emit(event: TraceEvent, turn?: number): void {
    if (this.internals.logCap !== undefined) {
      this.logEntries.push({ ...event, seq: this.logSeq++, ...(turn !== undefined ? { turn } : {}) });
      if (this.logEntries.length > this.internals.logCap) this.logEntries.splice(0, this.logEntries.length - this.internals.logCap);
    }
    // From a copy (ruling F): a handler that subscribes or unsubscribes during
    // an event changes who hears the NEXT one, never this one. A live Set
    // would call a handler added mid-delivery straight away.
    for (const handler of [...this.traceHandlers]) handler(event);
    this.internals.emitEngine(this.id, event, turn);
  }

  /** The retained flow log (opt-in via the Engine's `log`), oldest first,
   *  capped. The introspection seam for hosts and tools; the durable play
   *  history in a save stays `playLog` (schema 4) - the log is a
   *  flow-lifetime utility and is NOT saved. */
  log(): readonly LogEntry[] {
    return this.logEntries;
  }

  /** Empty the retained log; `seq` keeps counting, so ordering across a
   *  clear stays meaningful. */
  clearLog(): void {
    this.logEntries = [];
  }

  // --- expression plumbing ----------------------------------------------------

  private node(expr: Expression): ExprNode {
    let node = this.internals.nodeCache.get(expr);
    if (!node) {
      node = deserialiseAst(expr.ast);
      this.internals.nodeCache.set(expr, node);
    }
    return node;
  }

  /** Tag group names are box-scoped: two boxes may name a group the same way
   *  (schema 1 - boxes namespace their groups), so a name is only ever
   *  resolved inside the box being asked, never bundle-wide. Ids are
   *  project-unique and accepted here too, still confined to the box.
   *
   *  A box on the project map sees ONE namespace: its own groups, then the
   *  map's group (design/project-map-contract.md 3.1). A box that has not
   *  opted in does not see the map's name at all, so a peek naming it there
   *  is the ordinary unknown-group refusal. Own groups first is stated for
   *  determinism only: a bundle that loads never has the two share a name. */
  private groupInBox(box: Box<Expression>, ref: string): TagGroup | undefined {
    const groups = groupsOfBox(this.internals.bundle, box);
    return groups.find((g) => effectiveGameId(g) === ref)
      ?? groups.find((g) => g.id === ref);
  }

  /** Fold one play into the indexes. O(the card's tags), not O(the log). */
  private indexPlay(record: PlayRecord): void {
    this.playCount.set(record.card, (this.playCount.get(record.card) ?? 0) + 1);
    this.lastPlayOf.set(record.card, record);
    const entry = this.internals.cardsByGameId.get(record.card);
    if (!entry) return;
    for (const [groupId, tagIds] of Object.entries(entry.card.tags ?? {})) {
      for (const tagId of tagIds) {
        // Keyed by the PLAYED card's box: history is box-specific (D7).
        const key = tagKey(entry.box.id, groupId, tagId);
        this.tagPlayCount.set(key, (this.tagPlayCount.get(key) ?? 0) + 1);
        this.lastPlayInTag.set(key, record);
      }
    }
  }

  /** Rebuild the indexes from the log. Called wherever `playLog` is REPLACED
   *  rather than appended to, which is `restore` alone. */
  private rebuildPlayIndex(): void {
    this.playCount.clear();
    this.lastPlayOf.clear();
    this.tagPlayCount.clear();
    this.lastPlayInTag.clear();
    for (const record of this.playLog) this.indexPlay(record);
  }

  /** One host per box, built once.
   *
   *  The closures below read `this.playCount`, `this.turnCounts` and the rest
   *  LIVE, so a cached host answers with current state - which is what makes
   *  caching safe rather than a snapshot bug. Unreal did this from the start
   *  (`hostsByBox_`, built at flow construction) and the other three rebuilt a
   *  host, and its closures, on every `evalCtx` call: once per deck per ask,
   *  and once per surviving card in the eviction pass. Structural divergence
   *  in one port AND the allocation the audit flagged, so the other three
   *  copied it (2026-08-29). Lazy rather than eager, so a bundle's unvisited
   *  boxes cost nothing. */
  private hostsByBox = new Map<string, StoryletsHost>();

  private host(box: Box<Expression>): StoryletsHost {
    const cached = this.hostsByBox.get(box.id);
    if (cached !== undefined) return cached;
    const made = this.makeHost(box);
    this.hostsByBox.set(box.id, made);
    return made;
  }

  /** `box` is the box whose ask is being evaluated: the play-history
   *  functions take a bare group name, so it resolves there, and they count
   *  only that box's own plays (the box is in the index key). That was
   *  automatic while every group was a box's; a project-map zone is shared,
   *  and its history is still not (design/project-map-contract.md 3.7, D7).
   *  History is THIS flow's: countPlayed answers "have I done this". */
  private makeHost(box: Box<Expression>): StoryletsHost {
    /** A group NAME and tag name resolved in THIS box, as the index's key,
     *  with this box in it: a zone's plays in another box are not this box's
     *  history. Undefined when either name is unknown here, which answers
     *  "never".
     *
     *  MEMOISED per (group, tag): the answer depends only on the bundle, which
     *  never changes under a flow, and a history condition asks it once per
     *  candidate card per ask, each time a walk of the box's groups and then
     *  the group's tags. */
    const keys = new Map<string, string | undefined>();
    const keyOf = (group: string, tag: string): string | undefined => {
      const memo = `${group}\u001f${tag}`;
      if (keys.has(memo)) return keys.get(memo);
      const found = this.groupInBox(box, group);
      const t = found?.tags.find((v) => v.gameId === tag);
      const key = found && t ? tagKey(box.id, found.id, t.id) : undefined;
      keys.set(memo, key);
      return key;
    };
    /** Turns-since is measured on the played card's box's clock (3.4). */
    const since = (record: PlayRecord): number => {
      const entry = this.internals.cardsByGameId.get(record.card);
      if (!entry) return NEVER_PLAYED;
      return (this.turnCounts.get(entry.box.id) ?? 0) - record.turn;
    };
    return {
      nextRandom: () => this.prng.next(),
      countPlayed: (card) => this.playCount.get(card) ?? 0,
      turnsSincePlayed: (card) => {
        const last = this.lastPlayOf.get(card);
        return last ? since(last) : NEVER_PLAYED;
      },
      countPlayedIn: (group, tag) => {
        const key = keyOf(group, tag);
        return key === undefined ? 0 : this.tagPlayCount.get(key) ?? 0;
      },
      turnsSincePlayedIn: (group, tag) => {
        const key = keyOf(group, tag);
        const last = key === undefined ? undefined : this.lastPlayInTag.get(key);
        return last ? since(last) : NEVER_PLAYED;
      },
    };
  }

  /** The evaluation environment (schema 3.1/6.2): @box/@deck resolve to the
   *  card under evaluation; in hand-condition contexts @deck is an empty bag,
   *  so any reference is an eval error (missing-policy throw). Every scope
   *  is the flow's MERGED view - its own copies over the shared values,
   *  names disjoint - and each host scope reads through the engine's resolver. */
  private evalCtx(box: Box<Expression>, deck: Deck<Expression> | undefined, handEnv: HandEnv): EvalContext {
    const others = this.internals.registryView();
    return {
      scopes: {
        // Every other engine's game-wide scope first (every engine reads every
        // scope); this engine's own tokens are its merged views, over the top.
        ...others.scopes,
        ...this.internals.hostResolvers,
        story: this.storyReader,
        box: this.boxReaders.get(box.id) ?? EMPTY_SCOPE,
        deck: deck ? this.deckReaders.get(deck.id) ?? EMPTY_SCOPE : EMPTY_SCOPE,
        hand: handEnv.bag,
      },
      host: this.host(box) as unknown as Record<string, unknown>,
      // The quality channel, answering for THIS ask's box and deck. Only wired
      // when a quality exists, so a bundle without one evaluates byte-
      // identically to before the feature.
      ...(this.internals.hasQualities || others.qualities !== undefined ? {
        qualities: (scope: string, name: string): readonly string[] | undefined =>
          scope === "story" ? this.internals.ladders.story.get(name)
          : scope === "box" ? this.internals.ladders.box.get(box.id)?.get(name)
          : scope === "deck" && deck ? this.internals.ladders.deck.get(deck.id)?.get(name)
          // @hand is composed, so the ladder belongs to whatever supplied the
          // value THIS ask: the bound tag, or the asking hand. `sources` is
          // already the map that answers that, because write-back needs it.
          : scope === "hand" ? this.handLadder(handEnv, name)
          : this.internals.ladders.host.get(scope)?.get(name) ?? others.qualities?.(scope, name),
      } : {}),
    };
  }

  /** The ladder behind one composed @hand name, or undefined when the name is
   *  not a quality (or came from criteria, which are tag NAMES, never state). */
  private handLadder(handEnv: HandEnv, name: string): readonly string[] | undefined {
    const source = handEnv.sources.get(name);
    if (!source) return undefined;
    return source.kind === "value" ? this.internals.ladders.value.get(source.id)?.get(name)
      : source.kind === "hand" ? this.internals.ladders.hand.get(source.id)?.get(name)
      : undefined;
  }

  private eval(expr: Expression, ctx: EvalContext): ScalarValue {
    return evaluate(this.node(expr), ctx, storyletsDialect);
  }

  /** `node` names whose condition it is (`card <gameId>`, ...): a function, so
   *  the label is built only when an evaluation fails. */
  private passes(expr: Expression | undefined, ctx: EvalContext, node: () => string): boolean {
    if (!expr) return true;
    try {
      return conditionPasses(this.eval(expr, ctx));
    } catch (e) {
      // An eval error is never a silent pass: the card/deck is unavailable
      // (schema 3.1), and the game hears of it through onError, tracing or
      // not; the trace carries it too.
      const message = e instanceof Error ? e.message : String(e);
      const at = node();
      const source = (expr as { src?: unknown }).src;
      this.internals.onError({ flow: this.id, kind: "condition", node: at, ...(typeof source === "string" && source !== "" ? { source } : {}), message });
      if (this.tracing) {
        this.emit({ type: "diagnostic", where: at.startsWith("deck ") ? `${at} gate` : `${at} condition`, message });
      }
      return false;
    }
  }

  // --- resolving asks (schema 2.6 + 3.6) -----------------------------------------

  private tagByGameId(group: TagGroup, gameId: string): Tag | undefined {
    return group.tags.find((t) => t.gameId === gameId);
  }

  /** A deal's ask: the hand's template bindings + chosen tags, or its rule's
   *  bindings, plus the implicit home binding (schema 2.4). */
  private askForHand(hand: Hand<Expression>, box: Box<Expression>): AskDescriptor {
    const boundTags = new Map<string, string>();
    const askNames: Record<string, string> = {};
    /** A template instance's `chosen` and a standalone hand's rule
     *  `bindings` bind the same way, so one loop serves both: a card reading
     *  @hand.<group> must not care HOW the group got bound
     *  (design/hand-typing.md, the residues). */
    const bindAll = (bindings: Record<string, string> | undefined): void => {
      for (const [groupId, tagId] of Object.entries(bindings ?? {})) {
        // A hole filled from a property rather than with a tag: resolve it
        // now, before tag composition (4.6, the hand that moves).
        if (isHoleRef(tagId)) {
          this.fillHoleFromProperty(hand, groupId, tagId, boundTags, askNames);
          continue;
        }
        boundTags.set(groupId, tagId);
        const found = this.internals.groupsById.get(groupId);
        const tag = found?.group.tags.find((t) => t.id === tagId);
        if (found && tag) askNames[effectiveGameId(found.group)] = effectiveGameId(tag);
      }
    };
    let condition: Expression | undefined;
    if (hand.template !== undefined) {
      const template = this.internals.templatesById.get(hand.template);
      if (!template) throw new StoryletError(`hand "${effectiveGameId(hand)}": unknown template "${hand.template}"`);
      // A template's FIXED bindings bind and are not named in @hand.
      for (const [groupId, tagId] of Object.entries(template.bindings ?? {})) {
        boundTags.set(groupId, tagId);
      }
      bindAll(hand.chosen);
      condition = template.condition;
    } else {
      bindAll(hand.rule?.bindings);
      condition = hand.rule?.condition;
    }
    boundTags.set(PLACE_GROUP, hand.id);
    this.bindStateGroups(box, boundTags, askNames);
    return { box, hand, ...(condition !== undefined ? { condition } : {}), boundTags, askNames };
  }

  /** A peek's ask: raw criteria ({group gameId: tag gameId}), bindings only,
   *  no condition slot (schema 3.1; the boundary, Reboot 4). */
  private askForPeek(box: Box<Expression>, criteria: Record<string, string>): AskDescriptor {
    const boundTags = new Map<string, string>();
    const askNames: Record<string, string> = {};
    for (const [groupRef, tagRef] of Object.entries(criteria)) {
      if (groupRef === PLACE_GROUP) {
        const hand = this.internals.handsByGameId.get(tagRef) ?? this.internals.handsById.get(tagRef);
        if (!hand) throw new StoryletError(`peek: unknown hand "${tagRef}" in home criteria`);
        boundTags.set(PLACE_GROUP, hand.hand.id);
        continue;
      }
      const found = this.groupInBox(box, groupRef);
      if (!found) {
        throw new StoryletError(`peek: unknown tag group "${groupRef}" in box "${effectiveGameId(box)}"`);
      }
      const tag = this.tagByGameId(found, tagRef) ?? found.tags.find((t) => t.id === tagRef);
      if (!tag) throw new StoryletError(`peek: unknown tag "${tagRef}" in group "${effectiveGameId(found)}"`);
      boundTags.set(found.id, tag.id);
      askNames[effectiveGameId(found)] = effectiveGameId(tag);
    }
    this.bindStateGroups(box, boundTags, askNames);
    return { box, boundTags, askNames };
  }

  /**
   * Fill one hole from the property its value names: the hand that moves
   * (design/engine-server.md 4.6).
   *
   * The semantics are `bindStateGroups`' below, word for word, applied per
   * HOLE instead of per group: resolved at ask time, and a value naming no tag
   * leaves the hole UNBOUND (a wildcard) with a diagnostic rather than dealing
   * a silently empty hand. What is added is the `@hand` scope - the asking
   * hand's OWN declared state, read here from the flow's merged view (the
   * shared half under the flow's own, so a `shared: true` declaration moves
   * the hole for every flow and a per-flow one moves it for this flow alone).
   *
   * Read BEFORE tag composition, which is the whole reason it is safe: the
   * @hand bag a card sees is built from the bound tags, so resolving a hole
   * from it would be circular. A hand's own declarations are not, so they are.
   */
  private fillHoleFromProperty(
    hand: Hand<Expression>, groupId: string, ref: string,
    boundTags: Map<string, string>, askNames: Record<string, string>,
  ): void {
    const found = this.internals.groupsById.get(groupId);
    const groupName = found ? effectiveGameId(found.group) : groupId;
    const where = `hand ${effectiveGameId(hand)}, tag group ${groupName}`;
    const parsed = parseHoleRef(ref);
    if (!parsed) {
      this.emit({ type: "diagnostic", where, message: `"${ref}" is not a @hand, @world or @story property reference` });
      return;
    }
    if (!found) {
      this.emit({ type: "diagnostic", where, message: `"${ref}" fills a tag group that is not in this box` });
      return;
    }
    let value: ScalarValue | undefined;
    if (parsed.scope === "hand") {
      value = this.valuesOf("hand", hand.id)[parsed.name];
    } else {
      try {
        value = this.getProperty(`${parsed.scope}.${parsed.name}`);
      } catch {
        value = undefined;
      }
    }
    if (value === undefined) {
      this.emit({ type: "diagnostic", where, message: `"${ref}" names a property that is not declared` });
      return;
    }
    const wanted = typeof value === "string" ? value : String(value);
    const tag = found.group.tags.find((t) => effectiveGameId(t) === wanted);
    if (!tag) {
      this.emit({ type: "diagnostic", where, message: `${ref} is "${wanted}", which is not one of the tags of "${groupName}"` });
      return;
    }
    boundTags.set(groupId, tag.id);
    askNames[groupName] = effectiveGameId(tag);
  }

  /**
   * Bind every `boundBy` group in the box from the property it names.
   *
   * The gap this closes: only a hand could bind a group, and `deal` takes no
   * criteria, so an axis driven by state (an act, a chapter) had nowhere to
   * gate. Runs AFTER the hand's own bindings and never overwrites one: an
   * explicit binding is a deliberate act and beats a default.
   *
   * A value naming no tag in the group leaves the group UNBOUND rather than
   * matching nothing. Unbound is a wildcard, so the ask still deals; a silent
   * empty hand would look like content that does not exist, and the diagnostic
   * is what says otherwise.
   */
  private bindStateGroups(box: Box<Expression>, boundTags: Map<string, string>, askNames: Record<string, string>): void {
    // The box's own groups and, when it is on the project map, the map's
    // group: a `boundBy` there binds in every opted-in box (3.2).
    for (const group of groupsOfBox(this.internals.bundle, box)) {
      if (group.boundBy === undefined || boundTags.has(group.id)) continue;
      const ref = /^@(world|story)\.([a-z][a-z0-9_-]*)$/.exec(group.boundBy);
      if (!ref) {
        this.emit({ type: "diagnostic", where: `tag group ${effectiveGameId(group)}`, message: `boundBy "${group.boundBy}" is not a @world or @story property reference` });
        continue;
      }
      let value: ScalarValue | undefined;
      try {
        value = this.getProperty(`${ref[1]}.${ref[2]}`);
      } catch {
        this.emit({ type: "diagnostic", where: `tag group ${effectiveGameId(group)}`, message: `boundBy "${group.boundBy}" names a property that is not declared` });
        continue;
      }
      const wanted = typeof value === "string" ? value : String(value);
      const tag = group.tags.find((t) => effectiveGameId(t) === wanted);
      if (!tag) {
        this.emit({ type: "diagnostic", where: `tag group ${effectiveGameId(group)}`, message: `${group.boundBy} is "${wanted}", which is not one of its tags` });
        continue;
      }
      boundTags.set(group.id, tag.id);
      askNames[effectiveGameId(group)] = effectiveGameId(tag);
    }
  }

  // --- @hand composition (schema 3.6) -------------------------------------------

  /** A store's full value view for one owner: the shared half under the
   *  flow's half. Names are disjoint, so the spread is routing, not
   *  shadowing. */
  private valuesOf(kind: Exclude<PartitionKind, "story">, id: string): PropertyBag {
    // No prototype: `@hand.constructor` must find nothing, not Object's.
    return Object.assign(Object.create(null) as PropertyBag,
      this.internals.shared[kind].get(id)?.values ?? {},
      this.stores[kind].get(id)?.values ?? {});
  }

  private buildHandEnv(ask: AskDescriptor): HandEnv {
    // No prototype, as above: the composed @hand is read by plain index, so an
    // ordinary object would answer `@hand.constructor` with a function.
    const bag: PropertyBag = Object.create(null) as PropertyBag;
    const sources = new Map<string, HandSource>();

    // 1. Tag properties of every bound tag (home binds a hand, not a tag).
    for (const [groupId, tagId] of ask.boundTags) {
      if (groupId === PLACE_GROUP) continue;
      for (const [name, value] of Object.entries(this.valuesOf("value", tagId))) {
        bag[name] = value;
        sources.set(name, { kind: "value", id: tagId });
      }
    }
    // 2. Hand properties, when the ask is a deal.
    if (ask.hand) {
      for (const [name, value] of Object.entries(this.valuesOf("hand", ask.hand.id))) {
        bag[name] = value;
        sources.set(name, { kind: "hand", id: ask.hand.id });
      }
    }
    // 3. Chosen tags / criteria, by group name (the tag's gameId as value).
    for (const [name, value] of Object.entries(ask.askNames)) {
      bag[name] = value;
      sources.set(name, { kind: "criteria" });
    }
    return { bag, sources, boundTags: ask.boundTags };
  }

  // --- the ask (schema 3.1 + 3.2) ------------------------------------------------

  /** The claims ledger, derived from THIS flow's board: card id -> holding
   *  hands (schema 3.5). Claims are per flow - another flow holding the
   *  card is another playthrough, not a rival hand. */
  private claims(): Map<string, number> {
    const counts = new Map<string, number>();
    for (const contents of this.boardContents.values()) {
      for (const id of contents) counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    return counts;
  }

  /** @internal - every card id on THIS flow's board, one entry per holding
   *  hand. The engine sums these across live flows for the shared ledger. */
  heldCardIds(): string[] {
    return [...this.boardContents.values()].flat();
  }



  /** The claims step (schema 3.1 step 6) for one card, as a verdict or null
   *  for "available". Two caps apply to a shared card and they are different
   *  statements, so they get different verdicts: `copies` is your own board
   *  filling up, `sharedCopies` is somebody else already holding it, and a
   *  participant told "claimed" about a card sitting on another person's
   *  table would read it as an engine fault (design/shared-scarcity 9.3.1).
   *
   *  `mine` counts this flow's holdings, `world` every live flow's, and `own`
   *  what the hand being dealt holds already: those are the hand's to keep or
   *  give up as it re-ranks, so they claim nothing against it. */
  private claimVerdict(
    card: Card<Expression>,
    shared: boolean,
    mine: Map<string, number>,
    world: Map<string, number>,
    own = 0,
  ): "claimed" | "claimed-elsewhere" | null {
    const id = card.id;
    if ((mine.get(id) ?? 0) - own >= acrossLimit(card)) return "claimed";
    if (shared && (world.get(id) ?? 0) - own >= sharedCap(card)) return "claimed-elsewhere";
    return null;
  }

  /** Tag matching (schema 3.1 step 3): for every bound group the card lists
   *  the bound tag or omits the group (wildcard); the home group inverts -
   *  a homed card requires a matching home binding (schema 2.4). */
  private tagsMatch(card: Card<Expression>, boundTags: Map<string, string>): boolean {
    const home = card.tags?.[PLACE_GROUP];
    if (home !== undefined && home.length > 0) {
      const bound = boundTags.get(PLACE_GROUP);
      if (bound === undefined || !home.includes(bound)) return false;
    }
    for (const [groupId, tagId] of boundTags) {
      if (groupId === PLACE_GROUP) continue;
      const tags = card.tags?.[groupId];
      if (tags === undefined) {
        // Omission is a wildcard unless the group says otherwise. A required
        // group inverts it, which is what `place` has always done per card.
        if (this.internals.requiredGroups.has(groupId)) return false;
        continue;
      }
      if (!tags.includes(tagId)) return false;
    }
    return true;
  }

  /** Run one ask: availability filter then ranking. `claimed` decides the
   *  claims step (step 6) per card, returning the verdict that refused it or
   *  null for available. `trace` (when a subscriber exists) collects the
   *  per-card verdicts. */
  private runAsk(
    ask: AskDescriptor,
    claimed: (card: Card<Expression>, shared: boolean) => "claimed" | "claimed-elsewhere" | null,
    trace?: { id: string; verdict: TraceVerdict; priority?: number; specificity?: number }[],
    /** The cards the hand being dealt holds: they win ties, so a re-ranking
     *  deal never swaps one for an equal. */
    held?: ReadonlyMap<string, number>,
  ): { ordered: CardEntry[]; handEnv: HandEnv } {
    const { box } = ask;
    const handEnv = this.buildHandEnv(ask);
    // Identity on the trace is by gameId (4.4), so the helper takes the CARD
    // rather than an id: every call site had one in hand, and taking the id
    // was the whole of how the two vocabularies got mixed.
    const verdict = (card: Card<Expression>, v: TraceVerdict): void => {
      trace?.push({ id: effectiveGameId(card), verdict: v });
    };

    // The hand's condition: ask-constant, evaluated once (schema 3.1 step 4).
    if (!this.passes(ask.condition, this.evalCtx(box, undefined, handEnv), () => `hand ${ask.hand ? effectiveGameId(ask.hand) : ""}`)) {
      return { ordered: [], handEnv };
    }

    // Deck gates: evaluated once per ask, in deck (id) order (schema 2.5).
    const gateOk = new Map<string, boolean>();
    for (const deck of box.decks) {
      gateOk.set(deck.id, this.passes(deck.condition, this.evalCtx(box, deck, handEnv), () => `deck ${deck.gameId}`));
    }

    const turn = this.turnCounts.get(box.id) ?? 0;
    const scored: { entry: CardEntry; priority: number; spec: number }[] = [];
    for (const deck of box.decks) {
      // ONE context per deck, not per card. It is built from box, deck and
      // handEnv, none of which vary inside this loop, and a condition is a
      // read-only gate (schema 3.1), so nothing can write through it and make
      // sharing visible. Rebuilding it per card cost an EvalContext, a scopes
      // map and five bag wrappers each time: about half the garbage a peek over
      // a large box produced (port-review-2026-08.md, measured).
      const deckCtx = this.evalCtx(box, deck, handEnv);
      const deckShared = deck.shared ?? false;
      for (const card of deck.cards) {
        const shared = cardIsShared(card, deckShared);
        if (!gateOk.get(deck.id)) {
          verdict(card, "deck-gate");
          continue;
        }
        // Taken out of the world by somebody's shared one-shot. Checked
        // before the flow's own clock, because "cooldown" would point the
        // reader at a turn counter that has nothing to do with it.
        if (shared && this.engine.isTaken(card.id)) {
          verdict(card, "taken");
          continue;
        }
        if ((this.cooldowns[card.id] ?? 0) > turn) {
          verdict(card, "cooldown");
          continue;
        }
        if (!this.tagsMatch(card, handEnv.boundTags)) {
          verdict(card, "tags");
          continue;
        }
        // The label is only read when an eval THROWS and only when tracing, so
        // building it per card was pure waste on the path that matters. Built
        // when tracing is on, where the cost is already accepted.
        if (card.condition && !this.passes(card.condition, deckCtx, () => `card ${card.gameId}`)) {
          verdict(card, "condition");
          continue;
        }
        const refused = claimed(card, shared);   // claims, last (schema 3.1 step 6)
        if (refused) {
          verdict(card, refused);
          continue;
        }

        let priority: number;
        if (typeof card.priority === "number") {
          priority = card.priority;
        } else {
          try {
            const v = this.eval(card.priority, deckCtx);
            // NaN is a number to `typeof` and no rank to a sort (ruling G):
            // Inf - Inf, an overflow or a host value gets here.
            if (typeof v !== "number" || Number.isNaN(v)) {
              verdict(card, "priority");
              continue;
            }
            priority = v;
          } catch (e) {
            if (this.tracing) {
              this.emit({ type: "diagnostic", where: `card ${card.gameId} priority`, message: e instanceof Error ? e.message : String(e) });
            }
            verdict(card, "priority");
            continue;
          }
        }
        let spec = 0;
        if (box.ranking.specificity && card.condition) {
          const node = this.node(card.condition);
          spec = matchedSpecificity(node, (n) => {
            try {
              return conditionPasses(evaluate(n, deckCtx, storyletsDialect));
            } catch {
              return false;
            }
          });
        }
        scored.push({ entry: { card, deck, box }, priority, spec });
      }
    }

    scored.sort((a, b) => b.priority - a.priority || b.spec - a.spec);   // stable
    // Seeded shuffle of each maximal tie run; runs of 1 consume no draws.
    let i = 0;
    while (i < scored.length) {
      let j = i + 1;
      while (j < scored.length
        && scored[j]!.priority === scored[i]!.priority
        && scored[j]!.spec === scored[i]!.spec) j++;
      if (j - i > 1) {
        // Held cards first, in the order they were scored; then the rest,
        // shuffled, or drawn by weight when their weights differ. Held cards
        // take no draws, so a deal draws what it always drew.
        const run = orderRun(scored.slice(i, j), held, this.prng);
        // Written back element by element, as all three ports do. It used to be
        // `scored.splice(i, j - i, ...run)`, which SPREADS one argument per
        // element: with every card at the default priority 0 the tie run is the
        // whole list, and a box of 150 000 such cards threw `RangeError:
        // Maximum call stack size exceeded` rather than dealing. Far-fetched
        // for one box, but it was a crash where the ports had no limit, and the
        // reference is supposed to be the thing they are transliterated from.
        for (let k = 0; k < run.length; k++) scored[i + k] = run[k]!;
      }
      i = j;
    }
    for (const s of scored) {
      trace?.push({ id: effectiveGameId(s.entry.card), verdict: "dealt", priority: s.priority, specificity: s.spec });
    }
    return { ordered: scored.map((s) => s.entry), handEnv };
  }

  /** Flip eligible-but-not-taken trace entries to "capped". `taken` is keyed
   *  by GAMEID, as the trace rows are (4.4): the two must move together or
   *  every dealt card silently reads as capped. */
  private capTrace(
    trace: { id: string; verdict: TraceVerdict; priority?: number; specificity?: number }[],
    taken: ReadonlySet<string>,
  ): void {
    for (const entry of trace) {
      if (entry.verdict === "dealt" && !taken.has(entry.id)) entry.verdict = "capped";
    }
  }

  private view(entry: CardEntry): DealtCard {
    const { card } = entry;
    return {
      id: card.id,
      gameId: effectiveGameId(card),
      ...(card.title !== undefined ? { title: card.title } : {}),
      ...(card.purpose !== undefined ? { purpose: card.purpose } : {}),
      // Fields are copied: the game may change what it is handed, and the
      // bundle's own maps are every later deal's, in every engine on it (the
      // hands-on review, 2026-10, foundation 6). Extension values are NOT: they
      // are the extension runtime's data, read-only by contract, and can be
      // large, so a copy per deal would charge every game for a mistake good
      // readers don't make (a family ruling, 2026-10-28, as Patter's).
      ...(card.fields !== undefined ? { fields: structuredClone(card.fields) } : {}),
      ...(card.extensions !== undefined ? { extensions: card.extensions } : {}),
    };
  }

  private handCapacity(hand: Hand<Expression>): number {
    if (hand.slots !== undefined) return hand.slots;
    const declared = hand.template !== undefined
      ? this.internals.templatesById.get(hand.template)?.slots
      : hand.rule?.slots;
    return declared === undefined || declared === "unbounded" ? Infinity : declared;
  }

  private resolveHand(ref: string): { hand: Hand<Expression>; box: Box<Expression> } {
    const found = this.internals.handsByGameId.get(ref) ?? this.internals.handsById.get(ref);
    if (!found) throw new StoryletError(`unknown hand "${ref}"`);
    return found;
  }

  // --- host surface (schema 5) ---------------------------------------------------

  /** Look at the top of the stock through raw tag criteria (schema 3.1):
   *  claims respected, nothing registered, nothing left behind but the
   *  trace line. You can never play a card you only peeked. */
  peek(boxRef: string, criteria: Record<string, string> = {}, n?: number): RankedList {
    this.assertOpen();
    const box = this.resolveBox(boxRef);
    const ask = this.askForPeek(box, criteria);
    const claimCounts = this.claims();
    // Skipped outright when the bundle shares nothing, which is most bundles:
    // the ledger walks every live flow's whole board, and an empty map answers
    // every question the same way a computed one would.
    const worldClaims = this.internals.hasShared ? this.engine.sharedClaims() : new Map<string, number>();
    const trace = this.tracing ? [] : undefined;
    // A peek consumes NO draws (ruling A, 2026-10-06): its tie shuffle and any
    // random() in a condition run on a throwaway COPY of the flow's generator,
    // so looking at the stock any number of times leaves the next deal exactly
    // as it was, and the peek still answers as a deal would now. The host's
    // closures read `this.prng` live, which is what the swap relies on.
    const own = this.prng;
    this.prng = makePrng(own.state());
    let ordered: CardEntry[];
    try {
      ({ ordered } = this.runAsk(ask, (card, shared) => this.claimVerdict(card, shared, claimCounts, worldClaims), trace));
    } finally {
      this.prng = own;
    }
    // CLAMPED. `slice(0, -1)` drops the last card and returns the rest, so a
    // negative n answered with almost the whole list here while every port
    // returned nothing (2026-08-29).
    const listed = n === undefined ? ordered : ordered.slice(0, Math.max(n, 0));
    if (trace) {
      this.capTrace(trace, new Set(listed.map((e) => effectiveGameId(e.card))));
      this.emit({ type: "peek", box: effectiveGameId(box), criteria, cards: trace }, this.turnCounts.get(box.id) ?? 0);
    }
    return { box: effectiveGameId(box), cards: listed.map((e) => this.view(e)) };
  }

  /** Refresh one hand (schema 3.5); returns its new shape. */
  deal(handRef: string): DealtCard[] {
    this.assertOpen();
    const { hand } = this.resolveHand(handRef);
    return this.dealMany([handRef])[effectiveGameId(hand)] ?? [];
  }

  /** Drop the cards a hand holds that are no longer available to it, as a deal
   *  does before it fills, and say which went and why: the deal's first pass,
   *  and the load's check of what it put back (`dropUnqualified`). */
  private evictUnavailable(hand: Hand<Expression>, box: Box<Expression>): { card: string; reason: CheckReason }[] {
    const ask = this.askForHand(hand, box);
    const handEnv = this.buildHandEnv(ask);
    const conditionOk = this.passes(ask.condition, this.evalCtx(box, undefined, handEnv), () => `hand ${effectiveGameId(hand)}`);
    // ONE context per deck, as the fill builds them (see runAsk): the gate
    // and every surviving card of that deck are evaluated against it.
    const gateOk = new Map<string, boolean>();
    const deckCtx = new Map<string, EvalContext>();
    for (const deck of box.decks) {
      const ctx = this.evalCtx(box, deck, handEnv);
      deckCtx.set(deck.id, ctx);
      gateOk.set(deck.id, this.passes(deck.condition, ctx, () => `deck ${deck.gameId}`));
    }
    const turn = this.turnCounts.get(box.id) ?? 0;
    const evicted: { card: string; reason: CheckReason }[] = [];
    // The card is named by gameId (4.4). A card the build no longer has -
    // the `vanished` branch below - has no gameId left, so it is named by
    // the id the board carries, which is the rule a load report has always
    // used for the same reason.
    const evict = (cardId: string, reason: CheckReason): false => {
      const known = this.internals.cardsById.get(cardId);
      evicted.push({ card: known ? effectiveGameId(known.card) : cardId, reason });
      return false;
    };
    const survivors = (this.boardContents.get(hand.id) ?? []).filter((cardId) => {
      if (!conditionOk) return evict(cardId, "hand-condition");
      const entry = this.internals.cardsById.get(cardId);
      if (!entry) return evict(cardId, "vanished");   // edited content: dropped
      // Its deck is in ANOTHER box now (a load, resume or hot swap into an
      // edited build): gone from this hand's box, as a deleted card is
      // (ruling D). It was evicted as "deck-gate" before, by a missing map key.
      if (entry.box.id !== box.id) return evict(cardId, "vanished");
      if (!gateOk.get(entry.deck.id)) return evict(cardId, "deck-gate");
      // Taken out of the world by somebody's shared one-shot (ruling C):
      // checked where the fill checks it, after the gate and before the
      // flow's own clock, so a card held by one playthrough goes from its
      // hand once another has spent it, as a cooldown would evict it.
      if (cardIsShared(entry.card, entry.deck.shared ?? false) && this.engine.isTaken(cardId)) {
        return evict(cardId, "taken");
      }
      if ((this.cooldowns[cardId] ?? 0) > turn) return evict(cardId, "cooldown");
      if (!this.tagsMatch(entry.card, handEnv.boundTags)) return evict(cardId, "tags");
      // The label is read only when an eval throws AND tracing is on.
      if (!this.passes(entry.card.condition, deckCtx.get(entry.deck.id)!, () => `card ${entry.card.gameId}`)) {
        return evict(cardId, "condition");
      }
      return true;
    });
    this.boardContents.set(hand.id, survivors);
    return evicted;
  }

  /** @internal - after a load puts this flow's board back, drop every held
   *  card that no longer qualifies, as a deal would (the hands-on review,
   *  2026-10, foundation 7: a save from before a content edit could put back a
   *  card whose condition is now false, and nothing said so). Every hand, in
   *  id order, on a copy of the flow's generator, so the check draws nothing
   *  and the next deal is exactly what it would have been. */
  dropUnqualified(): { hand: string; card: string; reason: CheckReason }[] {
    const own = this.prng;
    this.prng = makePrng(own.state());
    try {
      const out: { hand: string; card: string; reason: CheckReason }[] = [];
      for (const handId of [...this.internals.handsById.keys()].sort()) {
        const { hand, box } = this.internals.handsById.get(handId)!;
        for (const e of this.evictUnavailable(hand, box)) out.push({ hand: effectiveGameId(hand), ...e });
      }
      return out;
    } finally {
      this.prng = own;
    }
  }

  /** Re-deal several / all hands (schema 3.5): seeded hand-order shuffle
   *  (fairness), evict, seed the ledger from survivors, fill in order.
   *  Returns the dealt slice - the new contents of exactly the hands this
   *  call dealt, keyed by hand gameId (board() stays the whole-board read).
   *
   *  The eviction pass evaluates each hand's condition and deck gates, and the
   *  fill evaluates them again, asking for the hand twice. That is the draw
   *  count of a deal and the corpus pins it: merging the two would move every
   *  random() in a gate and the count of diagnostics (engine review 2026-10,
   *  section 4). */
  dealMany(handRefs?: string[]): Record<string, DealtCard[]> {
    this.assertOpen();
    const dealt = (handRefs ?? [...this.internals.handsById.keys()].sort())
      .map((ref) => this.resolveHand(ref));
    shuffleInPlace(dealt, this.prng);

    // Eviction first: drop dealt cards no longer available to their hand
    // (minus the claims check against their own seat). Trace events fire after
    // the state they report has landed (a handler reading the board sees the
    // eviction).
    for (const { hand, box } of dealt) {
      const evicted = this.evictUnavailable(hand, box);
      if (this.tracing) {
        const turn = this.turnCounts.get(box.id) ?? 0;
        for (const e of evicted) this.emit({ type: "evict", hand: effectiveGameId(hand), card: e.card, reason: e.reason }, turn);
      }
    }

    const claimCounts = this.claims();
    // The world ledger is taken once for the whole batch and kept in step with
    // the local one below, so two hands in the SAME deal cannot both take the
    // last shared copy. Skipped outright when the bundle shares nothing (most
    // bundles): an empty map answers every question the same way.
    const worldClaims = this.internals.hasShared ? this.engine.sharedClaims() : new Map<string, number>();
    for (const { hand, box } of dealt) {
      // A deal makes the hand right (the hands-on review, 2026-10, ruling 1):
      // it ranks everything eligible, the cards it holds included, and the hand
      // becomes the top of that ranking, full or not. A held card wins a tie,
      // so an equal card never displaces it; one a better card displaces goes
      // back to the stock, "outranked".
      const held = this.boardContents.get(hand.id) ?? [];
      const own = new Map<string, number>();
      for (const id of held) own.set(id, (own.get(id) ?? 0) + 1);
      const ask = this.askForHand(hand, box);
      const trace = this.tracing ? [] : undefined;
      // At most `inOneHand` times in this hand; in one hand of this flow at a
      // time unless the card says any (or, from an older bundle, `copies`);
      // at most `sharedCopies` hands anywhere (schema 3.5, shared-scarcity 5).
      const { ordered } = this.runAsk(ask,
        (card, shared) => this.claimVerdict(card, shared, claimCounts, worldClaims, own.get(card.id) ?? 0), trace, own);
      // The top of the ranking, each card as many times as one hand may hold it.
      const want = new Map<string, number>();
      let room = this.handCapacity(hand);
      for (const e of ordered) {
        if (room <= 0) break;
        const n = Math.min(inOneHandOf(e.card), room);
        want.set(e.card.id, n);
        room -= n;
      }
      // Held cards keep their places; newcomers follow, in rank order.
      const next: string[] = [];
      const outranked: string[] = [];
      for (const id of held) {
        const n = want.get(id) ?? 0;
        if (n > 0) { next.push(id); want.set(id, n - 1); } else outranked.push(id);
      }
      for (const e of ordered) {
        for (let n = want.get(e.card.id) ?? 0; n > 0; n--) next.push(e.card.id);
        want.delete(e.card.id);
      }
      this.boardContents.set(hand.id, next);
      for (const id of held) {
        claimCounts.set(id, (claimCounts.get(id) ?? 1) - 1);
        worldClaims.set(id, (worldClaims.get(id) ?? 1) - 1);
      }
      for (const id of next) {
        claimCounts.set(id, (claimCounts.get(id) ?? 0) + 1);
        worldClaims.set(id, (worldClaims.get(id) ?? 0) + 1);
      }
      // Emitted after the hand is set: a handler reading board() sees the deal.
      if (trace) {
        const turn = this.turnCounts.get(box.id) ?? 0;
        for (const id of outranked) {
          this.emit({ type: "evict", hand: effectiveGameId(hand), card: effectiveGameId(this.internals.cardsById.get(id)!.card), reason: "outranked" }, turn);
        }
        this.capTrace(trace, new Set(next.map((id) => effectiveGameId(this.internals.cardsById.get(id)!.card))));
        this.emit({ type: "deal", hand: effectiveGameId(hand), cards: trace }, turn);
      }
    }

    return Object.fromEntries(dealt.map(({ hand }) => [
      effectiveGameId(hand),
      (this.boardContents.get(hand.id) ?? []).map((id) => this.view(this.internals.cardsById.get(id)!)),
    ]));
  }

  /** The board: current hand contents, in dealt order, keyed by hand gameId
   *  (schema 5). Read it for what is out; peek the stock for what could
   *  come.
   *
   *  `boxRef` (a box gameId or id) narrows the read to that box's hands, in
   *  the same shape and the same order: "give me the barks hands" is a
   *  common host query, and boxes are how a game separates its storylet
   *  systems, so the grouping belongs here rather than in every host. An
   *  unknown box throws, as it does on turn() and peek(). */
  board(boxRef?: string): Record<string, DealtCard[]> {
    this.assertOpen();
    const keep = boxRef !== undefined ? this.resolveBox(boxRef).id : undefined;
    return Object.fromEntries([...this.boardContents.entries()]
      .filter(([handId]) => keep === undefined || this.internals.handsById.get(handId)!.box.id === keep)
      .map(([handId, ids]) => [
        effectiveGameId(this.internals.handsById.get(handId)!.hand),
        ids.map((id) => this.view(this.internals.cardsById.get(id)!)),
      ]));
  }

  /** Resolve a played/inspected card within a hand on the board. */
  private resolveDealt(cardId: string, handRef: string): { entry: CardEntry; ask: AskDescriptor } {
    const entry = this.internals.cardsById.get(cardId) ?? this.internals.cardsByGameId.get(cardId);
    if (!entry) throw new StoryletError(`unknown card "${cardId}"`);
    const { hand, box } = this.resolveHand(handRef);
    if (!(this.boardContents.get(hand.id) ?? []).includes(entry.card.id)) {
      throw new StoryletError(`card "${effectiveGameId(entry.card)}" is not dealt to hand "${effectiveGameId(hand)}"`);
    }
    return { entry, ask: this.askForHand(hand, box) };
  }

  /** Outcome availability, evaluated against CURRENT state on every ask
   *  (schema 3.1/5) - never a deal-time snapshot. */
  outcomes(cardId: string, from: string): OutcomeView[] {
    this.assertOpen();
    const { entry, ask } = this.resolveDealt(cardId, from);
    const ctx = this.evalCtx(entry.box, entry.deck, this.buildHandEnv(ask));
    return entry.card.outcomes.map((o) => ({
      id: o.id,
      gameId: effectiveGameId(o),
      ...(o.title !== undefined ? { title: o.title } : {}),
      ...(o.purpose !== undefined ? { purpose: o.purpose } : {}),
      // Fields copied, as a dealt card's (`view`).
      ...(o.fields !== undefined ? { fields: structuredClone(o.fields) } : {}),
      ...(o.extensions !== undefined ? { extensions: o.extensions } : {}),   // read-only, as a dealt card's
      available: this.passes(o.condition, ctx, () => `outcome ${effectiveGameId(entry.card)}/${effectiveGameId(o)}`),
    }));
  }

  /** Apply an outcome (schema 3.7): the card must sit in a hand on the
   *  board (you never play a card from inside the deck). All-or-nothing:
   *  throws before any mutation on a gated-shut outcome or a refused write
   *  target, and a refusal changes nothing at all.
   *
   *  A card with NO outcomes is played with none, named as "" (the
   *  no-outcome-play brief, 2026-09-14): a masthead, a notice, a codex entry,
   *  whose play means "shown". It is everything a play is except the writes:
   *  the play log and the history functions count it, the box's turn moves by
   *  the usual rule, the redraw rests it and it leaves its hand. "" is the one
   *  spelling in all four runtimes, because a Blueprint pin cannot be absent.
   *  Only the empty-for-empty case is new: "" on a card that has outcomes is
   *  refused, and a named outcome on a card with none is refused as before. */
  play(cardId: string, outcomeGameId: string, from: string, opts: PlayOptions = {}): void {
    this.assertOpen();
    const { entry, ask } = this.resolveDealt(cardId, from);
    const bare = outcomeGameId === "";
    if (bare && entry.card.outcomes.length > 0) {
      throw new StoryletError(`card "${effectiveGameId(entry.card)}" has outcomes (${entry.card.outcomes.map((o) => effectiveGameId(o)).join(", ")}); name the one played`);
    }
    const outcome = bare ? undefined : entry.card.outcomes.find((o) => effectiveGameId(o) === outcomeGameId);
    if (!bare && !outcome) throw new StoryletError(`card "${effectiveGameId(entry.card)}" has no outcome "${outcomeGameId}"`);

    const handEnv = this.buildHandEnv(ask);
    const ctx = this.evalCtx(entry.box, entry.deck, handEnv);
    if (outcome && !this.passes(outcome.condition, ctx, () => `outcome ${effectiveGameId(entry.card)}/${outcomeGameId}`)) {
      throw new StoryletError(`outcome "${outcomeGameId}" on "${effectiveGameId(entry.card)}" is gated shut`);
    }

    // The played card's box's clock advances (schema 3.4); computed up
    // front so the play and its writes log as one action, one turn stamp.
    //
    // A TIMED box (design/engine-server.md 4.8) defaults to advancing
    // NOTHING: its clock is time, the host ticks it, and a play is not a
    // tick. A call that names `advanceTurns` still gets what it asked for,
    // in either kind of box, because the call says otherwise. This is the
    // whole of `turn`'s effect on the engine: the seconds are never read.
    const perPlay = entry.box.turn !== undefined ? 0 : this.internals.bundle.settings.playAdvancesTurns;
    const newTurn = (this.turnCounts.get(entry.box.id) ?? 0) + (opts.advanceTurns ?? perPlay);

    // A play is ALL-OR-NOTHING (ruling B, 2026-10-06). Every target is
    // resolved and checked first, so a refusal the engine can know in advance
    // lands nothing and emits nothing; then every right-hand side evaluates
    // against PRE-play state (schema 3.7); then the writes land. A refusal
    // only the landing can meet puts back what the earlier writes replaced.
    // The write events fire once all of them have landed.
    const changes = Object.entries(outcome?.changes ?? {})
      .map(([target, expr]) => ({ target, expr, plan: this.planWrite(target, entry, handEnv) }));
    const writes = changes.map((c) => ({ ...c, value: this.eval(c.expr, ctx) }));
    const landed: { target: string; plan: WritePlan; value: ScalarValue; prev: ScalarValue | undefined }[] = [];
    try {
      for (const w of writes) landed.push({ target: w.target, plan: w.plan, value: w.value, prev: this.landWrite(w.plan, w.value) });
    } catch (e) {
      for (const l of [...landed].reverse()) this.undoWrite(l.plan, l.prev);
      throw e;
    }
    if (this.tracing) {
      for (const { target, plan, value, prev } of landed) {
        this.emit({ type: "write", target, path: plan.path, value, ...(prev !== undefined ? { prev } : {}) }, newTurn);
      }
    }

    const outcomeId = outcome ? effectiveGameId(outcome) : "";
    const record: PlayRecord = { card: effectiveGameId(entry.card), outcome: outcomeId, turn: newTurn };
    this.playLog.push(record);
    this.indexPlay(record);
    if (entry.card.redraw === "never") {
      // A shared one-shot leaves the world rather than this flow: the engine
      // holds it, so every flow is refused. A finite redraw deliberately does
      // NOT share, whatever the deck says - a cooldown is an absolute turn of
      // this flow's box clock, and there is no shared clock to compare it
      // against (design/shared-scarcity.md 9.3.2).
      if (cardIsShared(entry.card, entry.deck.shared ?? false)) this.engine.markTaken(entry.card.id);
      else this.cooldowns[entry.card.id] = Number.MAX_SAFE_INTEGER;
    } else if (typeof entry.card.redraw === "number") {
      this.cooldowns[entry.card.id] = newTurn + entry.card.redraw;
    }
    // The card leaves its hand, releasing its claim (schema 3.5/3.7).
    const handId = ask.hand!.id;
    // One copy, for a card the hand holds more than once (`inOneHand`).
    const contents = this.boardContents.get(handId) ?? [];
    const at = contents.indexOf(entry.card.id);
    this.boardContents.set(handId, [...contents.slice(0, at), ...contents.slice(at + 1)]);
    this.turnCounts.set(entry.box.id, newTurn);
    // Emitted last: a handler reading the board and the clock sees the play.
    if (this.tracing) this.emit({ type: "play", card: effectiveGameId(entry.card), outcome: outcomeId, turn: newTurn }, newTurn);
  }

  /**
   * Take a card out of a hand without playing it: the player turned it down,
   * or the game wants the room (the hands-on review, 2026-10, ruling: discard).
   * No outcome, no play in the log, no move of the clock, no play count. It
   * starts its Redraw as a play would, so a re-ranking deal doesn't hand it
   * straight back: a finite Redraw waits that many turns from now, a "never"
   * card is gone for good (for everyone, when it's shared), and an "always"
   * card can come up at the next deal. One copy leaves, for a card the hand
   * holds more than once.
   */
  discard(cardId: string, from: string): void {
    this.assertOpen();
    const { entry, ask } = this.resolveDealt(cardId, from);
    const turn = this.turnCounts.get(entry.box.id) ?? 0;
    if (entry.card.redraw === "never") {
      if (cardIsShared(entry.card, entry.deck.shared ?? false)) this.engine.markTaken(entry.card.id);
      else this.cooldowns[entry.card.id] = Number.MAX_SAFE_INTEGER;
    } else if (typeof entry.card.redraw === "number") {
      this.cooldowns[entry.card.id] = turn + entry.card.redraw;
    }
    const handId = ask.hand!.id;
    const contents = this.boardContents.get(handId) ?? [];
    const at = contents.indexOf(entry.card.id);
    this.boardContents.set(handId, [...contents.slice(0, at), ...contents.slice(at + 1)]);
    if (this.tracing) this.emit({ type: "discard", hand: effectiveGameId(ask.hand!), card: effectiveGameId(entry.card), turn }, turn);
  }

  /** One owned property's address, owner segment and all (4.4). */
  private address(kind: OwnedScope, id: string): string {
    return addressOf(this.internals, kind, id);
  }

  /** Find the bag that declares one change's name: the flow's bag when the
   *  property is per-flow, the shared bag when it is shared - the
   *  union/partition invariant made executable. Read by OWN value, so a name
   *  Object.prototype carries is no property. Throws, landing nothing, when
   *  no bag declares it or the declaration is `writable: false`. */
  private planBag(kind: Exclude<PartitionKind, "story"> | "story", id: string | undefined, name: string, path: string): WritePlan {
    const own = kind === "story" ? this.stores.story : id !== undefined ? this.stores[kind].get(id) : undefined;
    const shared = kind === "story" ? this.internals.shared.story : id !== undefined ? this.internals.shared[kind].get(id) : undefined;
    const bag = ownValue(own, name) !== undefined ? own
      : ownValue(shared, name) !== undefined ? shared
      : undefined;
    if (bag === undefined) throw new StoryletError(`no property at "${path}"`);
    // The kernel would refuse this at `set`, with this text; asked here so the
    // refusal comes before any of the play's writes has landed.
    if (bag.declarations().some((d) => d.name === name && d.writable === false)) {
      throw new StoryletError(`'${name}' is read-only`);
    }
    return { kind: "bag", bag, name, path };
  }

  /** Resolve and check one change target, writing nothing: every refusal a
   *  write can meet that the engine can know in advance is met HERE, so a
   *  play checks all its targets before any write lands (ruling B). */
  private planWrite(target: string, entry: CardEntry, handEnv: HandEnv): WritePlan {
    const match = /^@([a-z][a-z0-9_]*)\.([A-Za-z_][A-Za-z0-9_-]*)$/.exec(target);
    if (!match) throw new StoryletError(`bad change target "${target}"`);
    const [, scope, name] = match as unknown as [string, string, string];
    const host = this.internals.hostScopes.get(scope);
    if (host !== undefined) {
      if (!host.set) throw new StoryletError(`@${scope}.${name} cannot be written: the host bound @${scope} read-only`);
      // The story's own promise, kept HERE and only here, because here is where
      // the story does the writing: it is consulted before the seam, so an
      // outcome never reaches the bag (which would let a host write past it)
      // nor a bound resolver (which cannot tell the two apart). The host's
      // setProperty is its own path and never asks. Patter's runtime refuses the
      // same write through the shared kernel, so both read one way.
      if (hostReadOnly(host.spec, name)) throw new StoryletError(`'@${scope}.${name}' is read-only (writable: false)`);
      return { kind: "host", host, name, path: `${scope}.${name}` };
    }
    switch (scope) {
      case "story": return this.planBag("story", undefined, name, `story.${name}`);
      case "box": return this.planBag("box", entry.box.id, name, `${this.address("box", entry.box.id)}.${name}`);
      case "deck": return this.planBag("deck", entry.deck.id, name, `${this.address("deck", entry.deck.id)}.${name}`);
      case "hand": {
        // Write-back routing (schema 3.6): the composed name remembers its
        // source store; writes to criteria/chosen-tag names are errors.
        const source = handEnv.sources.get(name);
        if (!source) throw new StoryletError(`@hand.${name} is not composed in this ask`);
        if (source.kind === "criteria") throw new StoryletError(`@hand.${name} is a chosen tag / criteria name and cannot be written`);
        return this.planBag(source.kind, source.id, name, `${this.address(source.kind, source.id)}.${name}`);
      }
      default: {
        // Another engine's game-wide scope (`@patter.x`): the family's shared
        // vocabulary lets a card write it, and the registry keeps that engine's
        // rules (a read-only property is refused when the write lands, and the
        // play then puts back what it had written). A story write, so no host flag.
        if (this.internals.registry.has(scope)) return { kind: "scope", scope, name, path: `${scope}.${name}` };
        if (this.internals.bundle.externalScopes?.includes(scope)) {
          throw new StoryletError(`@${scope}.${name} cannot be written: no engine on this registry registered @${scope}`);
        }
        throw new StoryletError(`bad change target scope "@${scope}"`);
      }
    }
  }

  /** Land one planned change; returns the value it replaced (for the log's
   *  "0 -> 1" reading). An engine write: the bag's subscribers fire (the
   *  firing rule). */
  private landWrite(plan: WritePlan, value: ScalarValue): ScalarValue | undefined {
    switch (plan.kind) {
      case "host": {
        const prev = plan.host.resolver.get(plan.name);
        plan.host.set!(plan.name, value);
        return prev;
      }
      case "bag": return plan.bag.set(plan.name, value).prev;
      case "scope": {
        const prev = this.internals.registry.get(plan.scope, plan.name);
        this.internals.registry.set(plan.scope, plan.name, value);
        return prev;
      }
    }
  }

  /** Put back what one landed change replaced, when a LATER change in the same
   *  play was refused as it landed (a refusal no plan could see coming: another
   *  engine's read-only property, a host resolver that throws). Silent and as
   *  the host, since this is the engine undoing itself, not the story writing. */
  private undoWrite(plan: WritePlan, prev: ScalarValue | undefined): void {
    if (prev === undefined) return;
    switch (plan.kind) {
      case "host": plan.host.set!(plan.name, prev, true); return;
      case "bag": plan.bag.set(plan.name, prev, { silent: true, reason: "play refused", host: true }); return;
      case "scope": this.internals.registry.set(plan.scope, plan.name, prev, { host: true }); return;
    }
  }

  /** Advance one box's clock (schema 3.4): a turn is one draw-from-stock
   *  session for that box, on THIS flow's clock. */
  advanceTurns(boxRef: string, n = 1): void {
    this.assertOpen();
    const box = this.resolveBox(boxRef);
    const next = (this.turnCounts.get(box.id) ?? 0) + n;
    this.turnCounts.set(box.id, next);
    if (this.tracing) this.emit({ type: "turns", box: effectiveGameId(box), turn: next }, next);
  }

  // --- state access (host surface + test tooling) ---------------------------------

  /** Every box, bundle order: identity + this flow's clock (the enumeration
   *  surface examiners key their turns sections on; parity member). */
  listBoxes(): BoxView[] {
    this.assertOpen();
    return this.internals.bundle.boxes.map((b) => ({
      id: b.id,
      gameId: effectiveGameId(b),
      ...(b.title !== undefined ? { title: b.title } : {}),
      turn: this.turnCounts.get(b.id) ?? 0,
      // Read-only and uncopied, as a dealt card's extension values (`view`).
      ...(b.extensions !== undefined ? { extensions: b.extensions } : {}),
    }));
  }

  /** THIS flow's kernel bags with their store path prefixes (the state
   *  logger's mount surface; parity member). The shared bags are the
   *  Engine's listBags; the flows are rebuilt by loadGame, so consumers
   *  re-enumerate after a load. */
  listBags(): BagMount[] {
    this.assertOpen();
    const mounts: BagMount[] = [{ prefix: "story", bag: this.stores.story }];
    for (const kind of OWNED_SCOPES) {
      for (const [id, bag] of this.stores[kind]) mounts.push({ prefix: addressOf(this.internals, kind, id), bag });
    }
    return mounts;
  }

  /** The flow's FULL merged view as examiner rows (the property examiner /
   *  editor surface, parity across all runtimes): the host scopes read
   *  through their resolvers, then per scope the shared values and this
   *  flow's own. Bundle order: host scopes (@world first when the bundle
   *  lists it first), story, then per-box / per-deck / per-hand / per-tag
   *  stores. */
  listProperties(): PropertyRow[] {
    this.assertOpen();
    const out: PropertyRow[] = hostRows(this.internals);
    const add = (_prefix: string, shared: StateBag | undefined, own: StateBag | undefined): void => {
      for (const bag of [shared, own]) {
        if (bag === undefined) continue;
        for (const row of bag.rows()) out.push(row);
      }
    };
    add("story", this.internals.shared.story, this.stores.story);
    for (const kind of OWNED_SCOPES) {
      const ids = new Set([...this.internals.shared[kind].keys(), ...this.stores[kind].keys()]);
      for (const id of ids) {
        add(addressOf(this.internals, kind, id), this.internals.shared[kind].get(id), this.stores[kind].get(id));
      }
    }
    return out;
  }

  /** Read by path: "world.x", "story.gold", "value.docks.danger",
   *  "box.village.heat", "deck.wares.n", "hand.the-elder.zone" - the flow's
   *  merged view, routed by the declaration's sharing.
   *
   *  The owner segment is the entity's GAMEID, the name it is called by
   *  everywhere else (4.4). Its internal id is accepted for this release and
   *  earns a `diagnostic` naming the address to move to; the next lockstep
   *  release refuses it. */
  getProperty(path: string): ScalarValue {
    this.assertOpen();
    const found = this.resolvePath(propertyPath(path));
    const value = found.kind === "host" ? found.host.resolver.get(found.name)
      : found.kind === "scope" ? this.internals.registry.get(found.token, found.name)
      : ownValue(found.own, found.name) ?? ownValue(found.shared, found.name);
    if (value === undefined) throw new StoryletError(`no property at "${path}"`);
    return value;
  }

  setProperty(path: string, value: ScalarValue): void {
    this.assertOpen();
    const found = this.resolvePath(propertyPath(path));
    checkHostWrite(this.internals, path, value);
    if (found.kind === "host") {
      if (!found.host.set) throw new StoryletError(`@${found.host.spec.token} is read-only here: the host bound no write`);
      found.host.set(found.name, value, true);
      return;
    }
    if (found.kind === "scope") {
      this.internals.registry.set(found.token, found.name, value, { host: true });
      return;
    }
    const bag = ownValue(found.own, found.name) !== undefined ? found.own
      : ownValue(found.shared, found.name) !== undefined ? found.shared
      : undefined;
    if (bag === undefined) throw new StoryletError(`no property at "${path}"`);
    // A host write: silent under the firing rule (no subscriber feedback
    // loop), visible to the bag's audit hook, and flagged HOST so a
    // `writable: false` does not refuse the game its own value.
    bag.set(found.name, value, { silent: true, reason: "host setProperty", host: true });
  }

  private resolvePath(path: string):
    | { kind: "host"; host: HostScopeState; name: string }
    | { kind: "scope"; token: string; name: string }
    | { kind: "bag"; own?: StateBag; shared?: StateBag; name: string } {
    const parts = path.split(".");
    const host = parts.length === 2 ? this.internals.hostScopes.get(parts[0]!) : undefined;
    if (host !== undefined) return { kind: "host", host, name: parts[1]! };
    if (parts.length === 2 && parts[0] !== "story" && this.internals.registry.has(parts[0]!)) {
      return { kind: "scope", token: parts[0]!, name: parts[1]! };
    }
    if (parts.length === 2 && parts[0] === "story") {
      return { kind: "bag", own: this.stores.story, shared: this.internals.shared.story, name: parts[1]! };
    }
    if (parts.length === 3 && isOwnedScope(parts[0])) {
      const kind = parts[0];
      const [, segment, name] = parts as unknown as [string, string, string];
      const owner = ownerOrThrow(this.internals, kind, segment, name);
      if (owner.legacy && this.tracing) {
        this.emit({ type: "diagnostic", where: "property address", message: legacyAddressMessage(this.internals, kind, segment, name) });
      }
      const own = this.stores[kind].get(owner.id);
      const shared = this.internals.shared[kind].get(owner.id);
      if (own === undefined && shared === undefined) throw new StoryletError(`no ${kind} store "${segment}"`);
      return { kind: "bag", ...(own !== undefined ? { own } : {}), ...(shared !== undefined ? { shared } : {}), name };
    }
    throw new StoryletError(`bad property path "${path}"`);
  }

  // --- persistence (schema 4) -------------------------------------------------

  /**
   * This flow's DURABLE HALF, the player's pocket (ruling H): every per-flow
   * `durable` property's value by its address, and every per-flow durable
   * one-shot this flow has spent, by card gameId. A later run opens the
   * player's flow with it, `openFlow(id, { durable })`; the shared half is
   * `engine.saveDurable()`.
   */
  saveDurable(): DurableSave {
    this.assertOpen();
    const { bundle, durable } = this.internals;
    return structuredClone({
      schema: DURABLE_SCHEMA,
      content: bundle.content,
      values: durableValues(this.stores, durable.flow),
      spent: durable.flowCards
        .filter((e) => this.cooldowns[e.card.id] === Number.MAX_SAFE_INTEGER)
        .map((e) => effectiveGameId(e.card)).sort(),
    });
  }

  /** @internal - write a planned pocket into this freshly opened flow
   *  (openFlow's `durable`, which has already checked and planned it). Its
   *  bags hold their defaults, so only what the plan carries is written. */
  writeDurable(plan: DurablePlan): void {
    for (const [prop, value] of plan.values) putDurable(durableBag(this.stores, prop), prop.decl.name, value);
    for (const cardId of plan.cards) this.cooldowns[cardId] = Number.MAX_SAFE_INTEGER;
  }

  /** @internal - this flow's blob: inside the engine's envelope without its
   *  properties (the registry has them), or parked whole by saveFlow.
   *  Engine-side plumbing; a host parks a flow with `engine.saveFlow`. Keyed
   *  by Game ID (save version 3; see savedCard): the turns by box, the
   *  cooldowns by card, the board by hand with its cards, in the order the
   *  flow holds them. */
  snapshot(withProps: boolean): FlowSave {
    const internals = this.internals;
    const boxKey = (id: string): string => {
      const box = internals.boxesById.get(id);
      return box !== undefined ? effectiveGameId(box) : id;
    };
    const handKey = (id: string): string => {
      const found = internals.handsById.get(id);
      return found !== undefined ? effectiveGameId(found.hand) : id;
    };
    return {
      ...(withProps ? { props: partitionValues(this.stores) } : {}),
      turns: Object.fromEntries([...this.turnCounts].map(([id, turn]) => [boxKey(id), turn])),
      prng: this.prng.state(),
      cooldowns: Object.fromEntries(Object.entries(this.cooldowns).map(([id, turn]) => [cardKey(internals, id), turn])),
      board: Object.fromEntries([...this.boardContents].map(([id, cards]) => [handKey(id), cards.map((c) => cardKey(internals, c))])),
      playLog: this.playLog,
    };
  }

  /** @internal - restore a freshly opened flow from its blob (loadGame, and
   *  openFlow's `restore`, both of which clean the blob first). Orphaned keys
   *  (deleted entities) drop; new declarations keep defaults. Engine-side
   *  plumbing that skips the checks a restore through `openFlow` makes, which
   *  is why a host resumes a flow through `openFlow` and never through this. */
  restore(saved: FlowSave): void {
    if (saved.props !== undefined) loadPartition(this.stores, saved.props);
    this.turnCounts = new Map(this.internals.bundle.boxes.map((b) => [b.id, 0]));
    for (const [boxId, turn] of Object.entries(saved.turns ?? {})) {
      if (this.turnCounts.has(boxId)) this.turnCounts.set(boxId, turn);
    }
    this.prng = makePrng(saved.prng);
    this.cooldowns = saved.cooldowns ?? {};
    this.playLog = saved.playLog ?? [];
    this.rebuildPlayIndex();
    this.boardContents = new Map(Object.entries(saved.board ?? {})
      .filter(([handId]) => this.internals.handsById.has(handId))
      .map(([handId, ids]) => [handId, ids.filter((id) => this.internals.cardsById.has(id))]));
    for (const handId of this.internals.handsById.keys()) {
      if (!this.boardContents.has(handId)) this.boardContents.set(handId, []);
    }
  }
}
